/**
 * Career Pathways Demo Data
 *
 * Client-specific mock data for demos. Switch between clients to show
 * industry-relevant roles, programs, and metrics.
 *
 * Also includes scenario system for demonstrating different user states:
 * - full: Power user with all benefits and history
 * - first-login: New employee seeing the platform for first time
 * - ta-only: Company only offers Tuition Assistance
 * - slrp-only: Company only offers Student Loan Repayment
 * - new-employee: 30 days in, some setup done
 * - maxed-out: Used all benefits this year
 */

import type { CompanySettingResource } from 'tio-data/data/resources/company-setting.type';
import type { PlanExtension as PlanModel } from 'tio-data/data/resources/plan.ext';

const hasOwn = <T extends object>(object: T, key: PropertyKey): key is keyof T =>
  Object.prototype.hasOwnProperty.call(object, key);

export type DemoScenarioId =
  | 'full'
  | 'first-login'
  | 'ta-only'
  | 'slrp-only'
  | 'no-ta'
  | 'new-employee'
  | 'maxed-out'
  | 'wellness'
  | 'pslf-wellness'
  | 'student-loan-contributions'
  | 'secure-2-match'
  | 'flexible-choice-plan';

export type DemoTasScenarioId =
  'not-started' | 'in-progress' | 'approved' | 'rejected' | 'completed';

export type DemoPslfScenarioId =
  | 'blank-slate'
  | 'borrower-signed'
  | 'employer-signed'
  | 'submitted'
  | 'form-rejected'
  | 'multi-form'
  | 'with-nslds'
  | 'full-journey';

// The demo "products" are ATOMIC benefits — each id IS a DemoScenario feature
// key, so the multi-select picker composes the UNION of the selected benefits'
// flags (see getCurrentDemoScenario). The pre-built bundles ('flexible-choice-plan'
// etc.) are gone as products; they survive only as LEGACY_PRODUCT_FLAGS decode
// targets for old deep links. DEMO_PRODUCT_IDS (below) is the canonical order.
export type DemoProductId =
  | 'tuitionAssistance'
  | 'studentLoanRepayment'
  | 'careerPathways'
  | 'financialWellness'
  | 'pslf'
  | 'employerMatch';

export type DemoSessionRoleId = 'employee' | 'supervisor' | 'admin';

export type DemoUserScenarioId =
  'first-login' | 'new-employee' | 'power-user' | 'benefits-maxed' | 'flex-choice-hsa';

export type DemoTaPaymentModelId = 'reimbursement' | 'prepayment' | 'reimbursement-with-deferral';

// The Debt axis: a single-select toggle over the employee's linked-loan profile.
// 'not-linked' is the always-present "no linked loans" state; the dollar levels
// carry a balance + 10-yr Standard monthly that drive the loan/SLR/IDR surfaces.
export type DemoDebtLevelId = 'not-linked' | 'low' | 'medium' | 'high';

export interface DemoScenario {
  id: DemoScenarioId;
  name: string;
  description: string;
  features: {
    tuitionAssistance: boolean;
    studentLoanRepayment: boolean;
    careerPathways: boolean;
    financialWellness: boolean;
    pslf: boolean;
    employerMatch: boolean;
  };
  userState: {
    isFirstLogin: boolean;
    daysEmployed: number;
    hasLinkedLoans: boolean;
    hasActiveTAApplication: boolean;
    benefitsMaxedOut: boolean;
    setupComplete: boolean;
    careerPlanCreated: boolean;
    skillsAssessed: boolean;
    taScenario?: DemoTasScenarioId;
  };
  messaging: {
    welcomeTitle: string;
    welcomeSubtitle: string;
    ctaText: string;
    ctaRoute?: string;
  };
}

export interface DemoProduct {
  id: DemoProductId;
  name: string;
  description: string;
  /** The DemoScenario feature flag this atomic product toggles (1:1 with `id`). */
  feature: keyof DemoScenario['features'];
  /** Default TAS sub-scenario — only meaningful on the `tuitionAssistance` product. */
  defaultTasScenarioId?: DemoTasScenarioId;
  /** Default PSLF sub-scenario — only meaningful on the `pslf` product. */
  defaultPslfScenarioId?: DemoPslfScenarioId;
}

export interface DemoSessionRole {
  id: DemoSessionRoleId;
  name: string;
  description: string;
  relationshipType: string;
}

export interface DemoUserScenario {
  id: DemoUserScenarioId;
  name: string;
  description: string;
  scenarioId: DemoScenarioId;
}

export interface DemoTaPaymentModel {
  id: DemoTaPaymentModelId;
  name: string;
  description: string;
}

export interface DemoDebtLevel {
  id: DemoDebtLevelId;
  name: string;
  description: string;
  /** Whether the employee has any linked student loans (false for 'not-linked'). */
  hasLinkedLoans: boolean;
  /**
   * Headline balance in DOLLARS — the reimagined loans page, the dashboard
   * snapshot, the IDR strings, and the SLR account `observation.balance` all read
   * dollars. `null` for 'not-linked' (no balance).
   */
  balanceDollars: number | null;
  /** 10-yr Standard monthly payment in DOLLARS (same dollar consumers). */
  monthlyPaymentDollars: number | null;
  /**
   * Loan-level `observation.balance` in CENTS (the SLR loan resource / loan model
   * `currentBalance` are cents). Always `balanceDollars * 100`.
   */
  balanceCents: number | null;
  /** SLR account `minimum-monthly-payment` in CENTS (the model divides by 100). */
  monthlyPaymentCents: number | null;
  /**
   * NOTE: balances/payments are deliberately NOT round numbers — sales feedback
   * is that even amounts read as fake. Each level's monthly payment is the real
   * 10-year standard amortization of its balance at its rate
   * (P·r/12 ÷ (1−(1+r/12)^−120)), so a prospect who checks the math finds it
   * consistent. Change a balance or rate ⇒ recompute the payment.
   */
  /** Repayment plan label, e.g. 'Standard'. */
  repaymentPlan: string;
  /** Representative APR used for the 10-yr Standard monthly. */
  interestRate: number;
}

export interface DemoSessionSelection {
  clientId: DemoClientId;
  /** The selected atomic benefits (non-empty). Order-insensitive; normalized. */
  productIds: DemoProductId[];
  roleId: DemoSessionRoleId;
  userScenarioId?: DemoUserScenarioId;
  taPaymentModelId?: DemoTaPaymentModelId;
  /** Explicit TA dashboard state; omitted = derived from the user scenario. */
  tasScenarioId?: DemoTasScenarioId;
  /** Explicit PSLF journey state; omitted = the product default. */
  pslfScenarioId?: DemoPslfScenarioId;
  debtLevelId?: DemoDebtLevelId;
}

/**
 * The applied-session echo (demoMode.currentSelection and the fixture
 * builders): every axis except tasScenarioId, which is write-only apply input
 * — storage can't tell an explicit pick from a derived value, so there is no
 * faithful value to echo.
 */
export type DemoSessionSelectionSnapshot = Required<Omit<DemoSessionSelection, 'tasScenarioId'>>;

/** The axes the company-setting blob is derived from. */
export type DemoSettingsSelection = Pick<
  DemoSessionSelectionSnapshot,
  'clientId' | 'productIds' | 'taPaymentModelId'
>;

// Plain demo settings payload — derives from the bare `*Resource` field type
// (not the legacy-mode record type, which brands `constructor`/`[Type]`).
export type DemoCompanySettings = Partial<CompanySettingResource>;
export type DemoPlanSummary = Pick<PlanModel, 'cadence' | 'isWellness'> & {
  id: string;
  name: string;
  annualLimit: number;
  lifetimeLimit: number;
  tiers: { amount: number }[];
};

export interface DemoPreRegisterCompany {
  id: string;
  name: string;
  launchDate: string;
  programTitle: string;
  taEnabled: boolean;
  settings: DemoCompanySettings;
}

export interface DemoPartnerCompany extends DemoPreRegisterCompany {
  companySetting: DemoCompanySettings;
  plans: DemoPlanSummary[];
}

// Canonical product order — drives DEMO_PRODUCT_LIST (the picker order), the
// serialized `demo_product` CSV, and the union iteration. `satisfies` keeps
// every id a real DemoScenario feature key, so a product IS a feature.
export const DEMO_PRODUCT_IDS = [
  'tuitionAssistance',
  'studentLoanRepayment',
  'careerPathways',
  'financialWellness',
  'pslf',
  'employerMatch',
] as const satisfies readonly (keyof DemoScenario['features'])[];

export const DEMO_PRODUCTS = {
  tuitionAssistance: {
    id: 'tuitionAssistance',
    name: 'Tuition Assistance',
    description: 'Apply for courses and education benefits',
    feature: 'tuitionAssistance',
    defaultTasScenarioId: 'in-progress',
  },
  studentLoanRepayment: {
    id: 'studentLoanRepayment',
    name: 'Student Loan Repayment',
    description: 'Employer student loan contributions',
    feature: 'studentLoanRepayment',
  },
  careerPathways: {
    id: 'careerPathways',
    name: 'Career Pathways',
    description: 'Skills, exploration, and mentoring',
    feature: 'careerPathways',
  },
  financialWellness: {
    id: 'financialWellness',
    name: 'Financial Wellness',
    description: 'Financial wellness journey',
    feature: 'financialWellness',
  },
  pslf: {
    id: 'pslf',
    name: 'PSLF',
    description: 'Public Service Loan Forgiveness guidance and forms',
    feature: 'pslf',
    defaultPslfScenarioId: 'full-journey',
  },
  employerMatch: {
    id: 'employerMatch',
    name: 'Secure 2.0 Match',
    description: 'Qualified student loan payment retirement match',
    feature: 'employerMatch',
  },
} satisfies Record<DemoProductId, DemoProduct>;

// Ordered by DEMO_PRODUCT_IDS (Object.values preserves insertion order, which
// matches the const above).
export const DEMO_PRODUCT_LIST = Object.values(DEMO_PRODUCTS);

// Decode table for LEGACY single-select `demo_product` values (the old bundle
// product ids) → the atomic benefit set, so pre-existing deep links / stored
// sessions still resolve. Mirrors each old bundle's DemoScenario feature set.
const LEGACY_PRODUCT_FLAGS: Record<string, DemoProductId[]> = {
  wellness: ['financialWellness'],
  'pslf-wellness': ['studentLoanRepayment', 'financialWellness', 'pslf'],
  'student-loan-contributions': ['studentLoanRepayment', 'financialWellness'],
  'secure-2-retirement-match': ['studentLoanRepayment', 'financialWellness', 'employerMatch'],
  'tuition-assistance': ['tuitionAssistance', 'careerPathways'],
  'flexible-choice-plan': [
    'tuitionAssistance',
    'studentLoanRepayment',
    'careerPathways',
    'financialWellness',
    'pslf',
    'employerMatch',
  ],
};

export const DEMO_SESSION_ROLES: Record<DemoSessionRoleId, DemoSessionRole> = {
  employee: {
    id: 'employee',
    name: 'Employee',
    description: 'Participant benefit experience',
    relationshipType: 'PARTICIPANT',
  },
  supervisor: {
    id: 'supervisor',
    name: 'Supervisor',
    description: 'Supervisor approval and team support experience',
    relationshipType: 'TAS.Approver.SUPERVISOR',
  },
  admin: {
    id: 'admin',
    name: 'Admin',
    description: 'Employer admin reporting and management experience',
    relationshipType: 'EMPLOYEE_ADMIN',
  },
};

export const DEMO_SESSION_ROLE_LIST = Object.values(DEMO_SESSION_ROLES);

export const DEMO_USER_SCENARIOS: Record<DemoUserScenarioId, DemoUserScenario> = {
  'first-login': {
    id: 'first-login',
    name: 'First Login',
    description: 'Brand new account, nothing configured yet',
    scenarioId: 'first-login',
  },
  'new-employee': {
    id: 'new-employee',
    name: 'New Employee',
    description: '30 days in, partial setup completed',
    scenarioId: 'new-employee',
  },
  'power-user': {
    id: 'power-user',
    name: 'Power User',
    description: 'Fully engaged employee with active benefits and history',
    scenarioId: 'full',
  },
  'benefits-maxed': {
    id: 'benefits-maxed',
    name: 'Benefits Maxed',
    description: 'Used all benefits available this year',
    scenarioId: 'maxed-out',
  },
  'flex-choice-hsa': {
    id: 'flex-choice-hsa',
    name: 'Flex Choice · HDHP / HSA',
    description: 'Early-career employee on an HDHP funding their HSA through Flex Choice',
    scenarioId: 'flexible-choice-plan',
  },
};

export const DEMO_USER_SCENARIO_LIST = Object.values(DEMO_USER_SCENARIOS);

export const DEMO_TA_PAYMENT_MODELS: Record<DemoTaPaymentModelId, DemoTaPaymentModel> = {
  reimbursement: {
    id: 'reimbursement',
    name: 'Reimbursement',
    description: 'Employee pays out of pocket and is reimbursed after course completion',
  },
  prepayment: {
    id: 'prepayment',
    name: 'Prepayment (CourseCash)',
    description: 'Employer prepays the school directly via CourseCash before the course starts',
  },
  'reimbursement-with-deferral': {
    id: 'reimbursement-with-deferral',
    name: 'Reimbursement w/ Deferral',
    description:
      'School defers tuition until after course start; employee is reimbursed within the deferral window',
  },
};

export const DEMO_TA_PAYMENT_MODEL_LIST = Object.values(DEMO_TA_PAYMENT_MODELS);

// 10-yr Standard monthly payments computed at 6.5% APR over 120 months:
//   $25,000 → $283.87 ≈ $284 · $50,000 → $567.74 ≈ $568 · $150,000 → $1,703.22 ≈ $1,703.
// Cents fields are exactly dollars × 100 (the SLR loan/account model splits units —
// see DemoDebtLevel doc comments and the contributions fixtures).
export const DEMO_DEBT_LEVELS: Record<DemoDebtLevelId, DemoDebtLevel> = {
  'not-linked': {
    id: 'not-linked',
    name: 'Loan not linked',
    description: 'No linked student loans',
    hasLinkedLoans: false,
    balanceDollars: null,
    monthlyPaymentDollars: null,
    balanceCents: null,
    monthlyPaymentCents: null,
    repaymentPlan: 'none',
    interestRate: 0,
  },
  low: {
    id: 'low',
    name: '$27,340 — 10-year Standard',
    description: '$27,340 balance on a 10-year Standard Repayment Plan',
    hasLinkedLoans: true,
    balanceDollars: 27340,
    monthlyPaymentDollars: 301.47,
    balanceCents: 2734000,
    monthlyPaymentCents: 30147,
    repaymentPlan: 'Standard',
    interestRate: 5.85,
  },
  medium: {
    id: 'medium',
    name: '$48,760 — 10-year Standard',
    description: '$48,760 balance on a 10-year Standard Repayment Plan',
    hasLinkedLoans: true,
    balanceDollars: 48760,
    monthlyPaymentDollars: 551.68,
    balanceCents: 4876000,
    monthlyPaymentCents: 55168,
    repaymentPlan: 'Standard',
    interestRate: 6.42,
  },
  high: {
    id: 'high',
    name: '$142,890 — 10-year Standard',
    description: '$142,890 balance on a 10-year Standard Repayment Plan',
    hasLinkedLoans: true,
    balanceDollars: 142890,
    monthlyPaymentDollars: 1664.97,
    balanceCents: 14289000,
    monthlyPaymentCents: 166497,
    repaymentPlan: 'Standard',
    interestRate: 7.08,
  },
};

export const DEMO_DEBT_LEVEL_LIST = Object.values(DEMO_DEBT_LEVELS);

// Every persisted demo-session key under the `reimagined-demo-*` namespace.
// `clearDemoSession()` removes this whole set. NOTE: the picker
// (`applyDemoSession`) additionally overloads the plain, non-namespaced `role`
// key with the demo relationship type, recording that it did so via
// `reimagined-demo-role-overloaded`. That `role` key is shared with the real
// user's role selection, so `clearDemoSession()` drops it ONLY when that overload
// marker is present — NOT for the in-page toggle or a pure `?demo=true` view,
// which never touch `role`.
const DEMO_MODE_STORAGE_KEYS = [
  'reimagined-demo-mode',
  'reimagined-demo-client',
  'reimagined-demo-product',
  'reimagined-demo-role',
  'reimagined-demo-role-overloaded',
  'reimagined-demo-scenario',
  'reimagined-demo-user-scenario',
  'reimagined-demo-ta-payment-model',
  'reimagined-demo-debt-level',
  'reimagined-demo-ta-scenario',
  'reimagined-demo-pslf-scenario',
];

function isReimaginedRoutePath(pathname: string): boolean {
  return pathname.split('/').includes('reimagined');
}

// The admin / partner-admin experience lives at the TOP-LEVEL /admin/* (the
// `authenticated` route segment is pathless, so it never appears in the URL). A
// sticky demo session must stay active across these routes too, so an admin-role
// demo (EMPLOYEE_ADMIN / TAS.Approver.SUPERVISOR) can click through the admin app
// without re-arming demo (?demo=true) on every link. Match only the FIRST path
// segment — not any nested segment that happens to equal "admin" (e.g. a record
// id) — to keep the sticky-demo boundary tight.
function isAdminRoutePath(pathname: string): boolean {
  return pathname.split('/').filter(Boolean)[0] === 'admin';
}

// The CURRENT shipped participant surface — the sales-facing demo target
// (#8022): first path segments of the authenticated participant tree
// (router.ts). A sticky demo session must stay ACTIVE across every route a
// rep can click into from the dashboard; if any shipped surface were missing
// here, clicking into it would drop the session into the paused
// "showing real data" state mid-pitch. Unfixtured pages render coherent
// empty states under the network-free block until their fixture phase lands.
const SHIPPED_PARTICIPANT_SEGMENTS = new Set([
  'dashboard',
  'account-activity',
  'pslf',
  'repaying-student-debt',
  'profile',
  'planning-for-college',
  'questions-you-have',
  'idr',
  'syf',
  'slr',
  'observability',
  'tas',
  'lpn',
  'wellness-insights',
  'college-planning-mentors',
  'contact-us',
]);

function isShippedParticipantRoutePath(pathname: string): boolean {
  const first = pathname.split('/').filter(Boolean)[0];
  return first !== undefined && SHIPPED_PARTICIPANT_SEGMENTS.has(first);
}

// Routes where a sticky (explicitly opted-in) demo session renders demo data:
// the CURRENT shipped participant app (the sales-facing demo), the classic
// admin experience, and the reimagined developer sandbox (which shares this
// demo data plane but is not part of the sales demo — #8022).
function isDemoableRoutePath(pathname: string): boolean {
  return (
    isShippedParticipantRoutePath(pathname) ||
    isReimaginedRoutePath(pathname) ||
    isAdminRoutePath(pathname)
  );
}

// Only an explicit `?demo=true` arms demo mode from the URL. The other `demo_*`
// params are session REFINEMENTS (client / product / role / scenario) that are
// consumed only once demo is already active — their bare presence in an
// incidental query string (a marketing or analytics link, a stale share) must
// NEVER activate demo for a real user. The canonical demo deep-link
// (`buildDemoSessionUrl`) always sets `demo=true`, so legitimate links and the
// picker are unaffected.
function hasDemoModeUrlParam(params: URLSearchParams): boolean {
  return params.get('demo') === 'true';
}

// Where the router is heading (a route name). Route models run before the URL
// updates, so location-based gates lag transitions; the demo-mode service
// keeps this current via noteDemoDestination.
let demoDestinationRouteName: string | null = null;

const SUBSTATE_NAME = /(^|[._])(loading|error)$/;

/** Record the router's transition destination so the demo gates don't lag the URL. */
export function noteDemoDestination(routeName: string | null | undefined): void {
  // Intermediate loading/error substates fire their own events — keep the real target.
  if (routeName && !SUBSTATE_NAME.test(routeName)) {
    demoDestinationRouteName = routeName;
  }
}

/** Clears the tracked destination (test isolation). */
export function resetDemoDestination(): void {
  demoDestinationRouteName = null;
}

/** The demoable scope answered by route NAME — same curated set, segments mirror names. */
export function isDemoableRouteName(name: string): boolean {
  // The pathless root hops straight into landingRoute; the session must survive it.
  if (name === 'authenticated' || name === 'authenticated.index') {
    return true;
  }
  if (!name.startsWith('authenticated.')) {
    return false;
  }
  const child = name.split('.')[1] ?? '';
  return SHIPPED_PARTICIPANT_SEGMENTS.has(child) || child === 'reimagined' || child === 'admin';
}

function hasStickyDemoMode(pathname: string): boolean {
  // The noted destination outranks the (lagging) location, in BOTH directions.
  const demoable = demoDestinationRouteName
    ? isDemoableRouteName(demoDestinationRouteName)
    : isDemoableRoutePath(pathname);
  return demoable && localStorage.getItem('reimagined-demo-mode') === 'true';
}

/**
 * Whether the current request should render demo data. This is a PURE READ —
 * it must never write persistent state. `?demo=true` activates demo for the
 * current navigation; the sticky flag keeps it active across the demoable
 * routes (the reimagined employee experience AND the /admin experience) ONLY
 * after a user has explicitly opted in via the picker (`applyDemoSession`) or
 * the reimagined demo toggle, and is cleared by `clearDemoSession()` on
 * logout / toggle-off.
 *
 * This function previously wrote the sticky flag as a side effect of merely
 * reading activation state, so a single stray demo link permanently armed demo
 * mode for a non-demo user — the root of the live regression where demo
 * fixtures (e.g. `demo-slr-company-1`) 404'd the app for real users. A
 * predicate named `is…()` must not mutate persisted state.
 */
export function isReimaginedDemoMode(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  if (hasDemoModeUrlParam(params)) {
    return true;
  }

  return hasStickyDemoMode(window.location.pathname);
}

/**
 * Clear ALL persisted demo-session state. Called on logout (so a demo session
 * never survives session invalidation, nor leaks to the next user on a shared
 * device) and by the manual toggle-off. A no-op outside the browser.
 */
export function clearDemoSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  // The plain, non-namespaced `role` key is shared with the real user's role
  // selection. ONLY the picker (`applyDemoSession`) overloads it with a demo
  // relationship type, and it stamps `reimagined-demo-role-overloaded` when it
  // does. Drop `role` only when that marker is present — captured BEFORE the loop
  // clears it. The in-page toggle and a pure `?demo=true` view never overload
  // `role`, so clearing it for them would wipe the real user's own selection
  // (and on a shared device it would survive past the demo). The sticky flag
  // alone is NOT a reliable proxy: the toggle sets it without overloading `role`.
  const roleWasOverloadedByDemo =
    localStorage.getItem('reimagined-demo-role-overloaded') === 'true';
  for (const key of DEMO_MODE_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  if (roleWasOverloadedByDemo) {
    localStorage.removeItem('role');
  }
}

// Every demo query param the session machinery writes (`buildDemoSessionUrl`,
// `setDemoScenarioId`, the reimagined header toggle). `exitDemoSession()`
// strips this whole set — a new axis param belongs here the moment
// `buildDemoSessionUrl` learns to set it (the unit suite asserts that
// invariant, so the list cannot silently drift per call site again).
export const DEMO_URL_PARAM_KEYS = [
  'demo',
  'demo_client',
  'demo_product',
  'demo_role',
  'demo_scenario',
  'demo_user_scenario',
  'demo_ta_payment_model',
  'demo_debt',
  'demo_ta_scenario',
  'demo_pslf_scenario',
] as const;

/**
 * Whether a sticky demo session is armed (explicitly opted in), regardless of
 * whether the CURRENT route renders demo data. Distinct from
 * {@link isReimaginedDemoMode}: the sticky flag is path-scoped, so on a
 * non-demoable route an armed session is "paused" and the page shows REAL
 * data. The demo pill keys its warning state on this distinction.
 */
export function isDemoSessionArmed(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return localStorage.getItem('reimagined-demo-mode') === 'true';
}

/**
 * The given URL with every demo param stripped — where {@link exitDemoSession}
 * lands. Pure and exported for tests and for callers that need the target
 * without navigating. Non-demo params and the hash are preserved.
 */
export function buildDemoExitUrl(currentHref: string): string {
  const url = new URL(currentHref);
  for (const key of DEMO_URL_PARAM_KEYS) {
    url.searchParams.delete(key);
  }
  return url.toString();
}

/**
 * THE canonical demo exit: wipe all persisted demo state and reload the
 * current route with every demo param stripped. Every exit affordance (the
 * demo pill, the picker footer, the reimagined header toggle) routes through
 * here so no call site hand-enumerates the param list — the header toggle's
 * hand-rolled copy had already drifted (it missed `demo_debt`).
 */
export function exitDemoSession(): void {
  if (typeof window === 'undefined') {
    return;
  }
  clearDemoSession();
  window.location.assign(buildDemoExitUrl(window.location.href));
}

/**
 * After a STICKY (picker-applied) session boots, absorb any demo params the
 * entry URL carried into storage and wipe them from the address bar. A rep
 * copying the URL mid-demo then shares a clean link instead of an armed one,
 * and demo params stop flowing into analytics page views.
 *
 * Deliberately a no-op for a non-sticky `?demo=true` view: there the params
 * ARE the session (pure-read activation never persists), so stripping them
 * would end the demo on the next transition. Safe ordering: every axis getter
 * persists-on-read, so reading them all first means the URL is redundant by
 * the time it is cleaned.
 */
export function stripDemoParamsAfterPersist(): void {
  if (typeof window === 'undefined' || !isDemoSessionArmed()) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  if (!DEMO_URL_PARAM_KEYS.some((key) => params.has(key))) {
    return;
  }
  // Absorb newer URL values into storage (each getter persists-on-read).
  getCurrentDemoClientId();
  getCurrentDemoProductIds();
  getCurrentDemoRoleId();
  getDemoScenarioIdFromUrl();
  getCurrentDemoUserScenarioId();
  getCurrentDemoTaPaymentModelId();
  getCurrentDemoDebtLevelId();
  getCurrentDemoTasScenarioId();
  getCurrentDemoPslfScenarioId();

  // Preserve any router/navigation metadata in history.state — we are only
  // cleaning the URL, not replacing the navigation entry's state.
  window.history.replaceState(window.history.state, '', buildDemoExitUrl(window.location.href));
}

const DEMO_SCENARIOS: Record<DemoScenarioId, DemoScenario> = {
  full: {
    id: 'full',
    name: 'Power User',
    description: 'Fully engaged employee with all features',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 730,
      hasLinkedLoans: true,
      hasActiveTAApplication: true,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'in-progress',
    },
    messaging: {
      welcomeTitle: 'Welcome back, Maria!',
      welcomeSubtitle: "You're making great progress on your goals",
      ctaText: 'View Dashboard',
    },
  },
  'first-login': {
    id: 'first-login',
    name: 'First Login',
    description: 'New employee seeing platform for first time',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: true,
      daysEmployed: 0,
      hasLinkedLoans: false,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: false,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Welcome to Your Benefits! 🎉',
      welcomeSubtitle: "Let's get you set up to maximize your education benefits",
      ctaText: 'Get Started',
      ctaRoute: 'authenticated.reimagined.index',
    },
  },
  'ta-only': {
    id: 'ta-only',
    name: 'TA Only',
    description: 'Company only offers Tuition Assistance',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: false,
      careerPathways: true,
      financialWellness: false,
      pslf: false,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 365,
      hasLinkedLoans: false,
      hasActiveTAApplication: true,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'in-progress',
    },
    messaging: {
      welcomeTitle: 'Ready to Learn?',
      welcomeSubtitle: 'Your employer offers up to $5,250 in tuition assistance',
      ctaText: 'Explore Programs',
    },
  },
  'slrp-only': {
    id: 'slrp-only',
    name: 'SLRP Only',
    description: 'Company only offers Student Loan Repayment',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 180,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Crushing Your Debt!',
      welcomeSubtitle: 'Your employer contributes $125/month toward your loans',
      ctaText: 'View Progress',
    },
  },
  'no-ta': {
    id: 'no-ta',
    name: 'No TA',
    description: 'Tuition Assistance not available to this employee',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 365,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Welcome Back!',
      welcomeSubtitle:
        'Your employer offers student loan repayment and career development benefits',
      ctaText: 'View Benefits',
    },
  },
  'new-employee': {
    id: 'new-employee',
    name: 'New Employee',
    description: '30 days in, some setup completed',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 30,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: false,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: "You're Off to a Great Start!",
      welcomeSubtitle: 'Complete your profile to unlock personalized recommendations',
      ctaText: 'Continue Setup',
    },
  },
  'maxed-out': {
    id: 'maxed-out',
    name: 'Benefits Maxed',
    description: 'Used all benefits this year',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 1095,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: true,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'completed',
    },
    messaging: {
      welcomeTitle: 'Amazing Year! 🏆',
      welcomeSubtitle: "You've maximized your 2026 benefits. Plan for next year!",
      ctaText: 'Plan for 2027',
    },
  },
  wellness: {
    id: 'wellness',
    name: 'Wellness',
    description: 'Financial wellness benefit focus',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: false,
      careerPathways: false,
      financialWellness: true,
      pslf: false,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 180,
      hasLinkedLoans: false,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Build Financial Confidence',
      welcomeSubtitle: 'Explore personalized wellness steps for your goals',
      ctaText: 'View Wellness Plan',
    },
  },
  'pslf-wellness': {
    id: 'pslf-wellness',
    name: 'PSLF Wellness',
    description: 'Loan wellness with PSLF support',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: true,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 365,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Stay on Track for Forgiveness',
      welcomeSubtitle: 'Review your PSLF progress and next best action',
      ctaText: 'Review PSLF Progress',
    },
  },
  'student-loan-contributions': {
    id: 'student-loan-contributions',
    name: 'Student Loan Contributions',
    description: 'Employer contribution benefit focus',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: false,
      employerMatch: false,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 270,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Your Employer Is Helping Pay Down Debt',
      welcomeSubtitle: 'See contributions, loan progress, and payoff impact',
      ctaText: 'View Contributions',
    },
  },
  'secure-2-match': {
    id: 'secure-2-match',
    name: 'Secure 2.0 Retirement Match',
    description: 'Student loan payment match toward retirement',
    features: {
      tuitionAssistance: false,
      studentLoanRepayment: true,
      careerPathways: false,
      financialWellness: true,
      pslf: false,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 540,
      hasLinkedLoans: true,
      hasActiveTAApplication: false,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: false,
      skillsAssessed: false,
      taScenario: 'not-started',
    },
    messaging: {
      welcomeTitle: 'Turn Loan Payments into Retirement Progress',
      welcomeSubtitle: 'Track qualifying payments and projected match value',
      ctaText: 'View Match Strategy',
    },
  },
  'flexible-choice-plan': {
    id: 'flexible-choice-plan',
    name: 'Flexible Choice Plan',
    description: 'Multi-benefit plan with flexible education choices',
    features: {
      tuitionAssistance: true,
      studentLoanRepayment: true,
      careerPathways: true,
      financialWellness: true,
      pslf: true,
      employerMatch: true,
    },
    userState: {
      isFirstLogin: false,
      daysEmployed: 730,
      hasLinkedLoans: true,
      hasActiveTAApplication: true,
      benefitsMaxedOut: false,
      setupComplete: true,
      careerPlanCreated: true,
      skillsAssessed: true,
      taScenario: 'approved',
    },
    messaging: {
      welcomeTitle: 'Choose the Benefit Path That Fits Today',
      welcomeSubtitle: 'Compare education, loan, and retirement-match opportunities',
      ctaText: 'Explore Choices',
    },
  },
};

export type DemoClientId =
  | 'lifepoint'
  | 'advocate'
  | 'colorado'
  | 'starbucks'
  | 'delaware'
  | 'university-of-miami'
  | 'shelby-county'
  | 'verizon'
  | 'ups'
  | 'daffy'
  | 'legal';

/**
 * Employer tax/profit status — the real-world driver of PSLF eligibility. PSLF
 * qualifies employment at non-profit (501c3) and government employers, NOT
 * for-profit ones, so `for-profit` personas mask the `pslf` benefit (see
 * personaMaskedFeatures) and the company-settings employer block.
 */
export type DemoEmployerType = 'for-profit' | 'non-profit' | 'government';

export interface DemoClient {
  id: DemoClientId;
  name: string;
  industry: string;
  logo?: string;
  employeeCount: number;
  taUtilization: number;
  planActivation: number;
  completionRate: number;
  internalMobility: number;
  /**
   * Employer-archetype label for the persona picker (e.g. "For-profit health
   * system"). Only set on the curated persona clients shown in DEMO_CLIENT_LIST.
   */
  personaLabel?: string;
}

export interface DemoRole {
  id: string;
  title: string;
  department: string;
  level: string;
  salary: string;
  openings: number;
  growth: string;
  skills: string[];
}

export interface DemoProgram {
  name: string;
  provider: string;
  type: string;
  duration: string;
  cost: string;
  completionRate: number;
  roi: number;
  enrollments: number;
}

export interface DemoPipelineStage {
  name: string;
  count: number;
  description: string;
}

export interface DemoCriticalRole {
  title: string;
  gap: number;
  risk: 'critical' | 'high' | 'medium' | 'low';
  retirements: number;
  pipeline: number;
}

export interface DemoCompetency {
  name: string;
  avgScore: number;
  target: number;
  gap: number;
}

export interface DemoClientData {
  client: DemoClient;
  criticalRoles: DemoCriticalRole[];
  topPrograms: DemoProgram[];
  pipeline: {
    identified: number;
    inProgram: number;
    developing: number;
    readyNow: number;
    promoted: number;
    attrition: number;
  };
  competencies: DemoCompetency[];
  departments: Array<{ name: string; employees: number; pipeline: number }>;
  employee?: {
    firstName: string;
    lastName: string;
  };
}

const LIFEPOINT_HEALTH: DemoClientData = {
  client: {
    id: 'lifepoint',
    name: 'Lifepoint Health',
    industry: 'Healthcare',
    personaLabel: 'For-profit health system',
    employeeCount: 50000,
    taUtilization: 18,
    planActivation: 27,
    completionRate: 79,
    internalMobility: 14,
  },
  criticalRoles: [
    { title: 'Registered Nurse (RN)', gap: 520, risk: 'critical', retirements: 135, pipeline: 245 },
    { title: 'Clinical Supervisor', gap: 72, risk: 'high', retirements: 20, pipeline: 38 },
    { title: 'Medical Assistant', gap: 180, risk: 'high', retirements: 48, pipeline: 110 },
    { title: 'Imaging Technologist', gap: 64, risk: 'medium', retirements: 18, pipeline: 34 },
    { title: 'Revenue Cycle Specialist', gap: 45, risk: 'medium', retirements: 12, pipeline: 28 },
  ],
  topPrograms: [
    {
      name: 'RN-to-BSN Completion',
      provider: 'Western Governors University',
      type: 'Degree',
      duration: '18 months',
      cost: '$10,500',
      completionRate: 84,
      roi: 238,
      enrollments: 420,
    },
    {
      name: 'Clinical Leadership Essentials',
      provider: 'Internal',
      type: 'Development',
      duration: '10 weeks',
      cost: '$2,200',
      completionRate: 90,
      roi: 275,
      enrollments: 310,
    },
    {
      name: 'Medical Assistant Certification',
      provider: 'NHA',
      type: 'Certificate',
      duration: '5 months',
      cost: '$1,800',
      completionRate: 88,
      roi: 210,
      enrollments: 260,
    },
    {
      name: 'Radiologic Technology Bridge',
      provider: 'Community College Network',
      type: 'Certificate',
      duration: '12 months',
      cost: '$6,500',
      completionRate: 76,
      roi: 195,
      enrollments: 95,
    },
    {
      name: 'Healthcare Revenue Cycle',
      provider: 'HFMA',
      type: 'Certificate',
      duration: '8 weeks',
      cost: '$1,100',
      completionRate: 92,
      roi: 180,
      enrollments: 140,
    },
  ],
  pipeline: {
    identified: 1650,
    inProgram: 980,
    developing: 360,
    readyNow: 120,
    promoted: 88,
    attrition: 150,
  },
  competencies: [
    { name: 'Patient Care', avgScore: 86, target: 94, gap: 8 },
    { name: 'Clinical Judgment', avgScore: 78, target: 88, gap: 10 },
    { name: 'Team Leadership', avgScore: 70, target: 84, gap: 14 },
    { name: 'Care Coordination', avgScore: 74, target: 86, gap: 12 },
    { name: 'Technology Adoption', avgScore: 68, target: 82, gap: 14 },
  ],
  departments: [
    { name: 'Nursing', employees: 21000, pipeline: 620 },
    { name: 'Clinical Operations', employees: 9500, pipeline: 230 },
    { name: 'Ambulatory Care', employees: 7200, pipeline: 180 },
    { name: 'Diagnostics', employees: 5100, pipeline: 85 },
    { name: 'Administrative Services', employees: 7200, pipeline: 65 },
  ],
};

const ADVOCATE_HEALTH: DemoClientData = {
  client: {
    id: 'advocate',
    name: 'Advocate Health',
    personaLabel: 'Non-profit health system',
    industry: 'Healthcare',
    employeeCount: 155000,
    taUtilization: 15,
    planActivation: 23,
    completionRate: 78,
    internalMobility: 12,
  },
  criticalRoles: [
    {
      title: 'Registered Nurse (RN)',
      gap: 1250,
      risk: 'critical',
      retirements: 340,
      pipeline: 520,
    },
    { title: 'Nurse Manager', gap: 85, risk: 'high', retirements: 28, pipeline: 45 },
    { title: 'Clinical Pharmacist', gap: 42, risk: 'high', retirements: 15, pipeline: 18 },
    { title: 'Medical Technologist', gap: 78, risk: 'medium', retirements: 22, pipeline: 35 },
    { title: 'Respiratory Therapist', gap: 34, risk: 'medium', retirements: 12, pipeline: 20 },
  ],
  topPrograms: [
    {
      name: 'RN-to-BSN Completion',
      provider: 'Chamberlain University',
      type: 'Degree',
      duration: '18 months',
      cost: '$12,000',
      completionRate: 82,
      roi: 245,
      enrollments: 1240,
    },
    {
      name: 'Nurse Leadership Certificate',
      provider: 'AONL',
      type: 'Certificate',
      duration: '6 months',
      cost: '$3,500',
      completionRate: 91,
      roi: 312,
      enrollments: 380,
    },
    {
      name: 'Clinical Ladder Advancement',
      provider: 'Internal',
      type: 'Development',
      duration: '12 months',
      cost: '$2,000',
      completionRate: 88,
      roi: 278,
      enrollments: 520,
    },
    {
      name: 'MSN - Nurse Practitioner',
      provider: 'Loyola University',
      type: 'Graduate',
      duration: '24 months',
      cost: '$45,000',
      completionRate: 76,
      roi: 185,
      enrollments: 145,
    },
    {
      name: 'Pharmacy Technician Cert',
      provider: 'ExCPT',
      type: 'Certificate',
      duration: '4 months',
      cost: '$1,200',
      completionRate: 94,
      roi: 220,
      enrollments: 210,
    },
  ],
  pipeline: {
    identified: 2800,
    inProgram: 1850,
    developing: 620,
    readyNow: 185,
    promoted: 142,
    attrition: 280,
  },
  competencies: [
    { name: 'Clinical Excellence', avgScore: 82, target: 90, gap: 8 },
    { name: 'Patient Safety', avgScore: 88, target: 95, gap: 7 },
    { name: 'Team Leadership', avgScore: 72, target: 85, gap: 13 },
    { name: 'Change Management', avgScore: 65, target: 80, gap: 15 },
    { name: 'Financial Acumen', avgScore: 58, target: 75, gap: 17 },
    { name: 'Technology Adoption', avgScore: 70, target: 85, gap: 15 },
  ],
  departments: [
    { name: 'Nursing', employees: 68000, pipeline: 1200 },
    { name: 'Clinical Services', employees: 25000, pipeline: 380 },
    { name: 'Pharmacy', employees: 8500, pipeline: 120 },
    { name: 'Imaging & Diagnostics', employees: 12000, pipeline: 95 },
    { name: 'Administration', employees: 18000, pipeline: 55 },
  ],
};

const UPS_DATA: DemoClientData = {
  client: {
    id: 'ups',
    name: 'UPS',
    industry: 'Logistics & Delivery',
    employeeCount: 534000,
    taUtilization: 8,
    planActivation: 18,
    completionRate: 71,
    internalMobility: 22,
  },
  criticalRoles: [
    { title: 'Package Car Driver', gap: 4200, risk: 'critical', retirements: 1800, pipeline: 2100 },
    { title: 'Hub Supervisor', gap: 380, risk: 'high', retirements: 95, pipeline: 220 },
    {
      title: 'Feeder Driver (CDL-A)',
      gap: 1100,
      risk: 'critical',
      retirements: 420,
      pipeline: 380,
    },
    { title: 'Operations Manager', gap: 125, risk: 'medium', retirements: 45, pipeline: 85 },
    { title: 'Industrial Engineer', gap: 65, risk: 'medium', retirements: 18, pipeline: 42 },
  ],
  topPrograms: [
    {
      name: 'CDL-A Training Program',
      provider: 'UPS Integrad',
      type: 'License',
      duration: '6 weeks',
      cost: '$8,000',
      completionRate: 88,
      roi: 420,
      enrollments: 3200,
    },
    {
      name: 'Supervisor Leadership',
      provider: 'Internal',
      type: 'Development',
      duration: '12 weeks',
      cost: '$2,500',
      completionRate: 92,
      roi: 385,
      enrollments: 1800,
    },
    {
      name: 'Supply Chain Management',
      provider: 'ASU Online',
      type: 'Degree',
      duration: '24 months',
      cost: '$18,000',
      completionRate: 68,
      roi: 195,
      enrollments: 420,
    },
    {
      name: 'Six Sigma Green Belt',
      provider: 'ASQ',
      type: 'Certificate',
      duration: '3 months',
      cost: '$2,800',
      completionRate: 85,
      roi: 245,
      enrollments: 680,
    },
    {
      name: 'Industrial Engineering Tech',
      provider: 'Purdue Global',
      type: 'Degree',
      duration: '36 months',
      cost: '$28,000',
      completionRate: 62,
      roi: 168,
      enrollments: 185,
    },
  ],
  pipeline: {
    identified: 8500,
    inProgram: 5200,
    developing: 1850,
    readyNow: 620,
    promoted: 485,
    attrition: 720,
  },
  competencies: [
    { name: 'Safety & Compliance', avgScore: 92, target: 98, gap: 6 },
    { name: 'Operations Excellence', avgScore: 85, target: 90, gap: 5 },
    { name: 'Team Leadership', avgScore: 72, target: 85, gap: 13 },
    { name: 'Technology Systems', avgScore: 68, target: 82, gap: 14 },
    { name: 'Customer Service', avgScore: 78, target: 88, gap: 10 },
    { name: 'Problem Solving', avgScore: 75, target: 85, gap: 10 },
  ],
  departments: [
    { name: 'Package Operations', employees: 285000, pipeline: 4800 },
    { name: 'Feeders & Transportation', employees: 125000, pipeline: 2100 },
    { name: 'Hub Operations', employees: 85000, pipeline: 1200 },
    { name: 'Engineering', employees: 12000, pipeline: 280 },
    { name: 'Corporate & Admin', employees: 27000, pipeline: 120 },
  ],
};

const STARBUCKS_DATA: DemoClientData = {
  client: {
    id: 'starbucks',
    name: 'Starbucks',
    personaLabel: 'For-profit — retail / hospitality',
    industry: 'Retail & Food Service',
    employeeCount: 402000,
    taUtilization: 42,
    planActivation: 38,
    completionRate: 74,
    internalMobility: 35,
  },
  criticalRoles: [
    { title: 'Store Manager', gap: 850, risk: 'critical', retirements: 120, pipeline: 680 },
    { title: 'Shift Supervisor', gap: 2100, risk: 'high', retirements: 180, pipeline: 1850 },
    { title: 'District Manager', gap: 95, risk: 'high', retirements: 35, pipeline: 72 },
    { title: 'Regional Director', gap: 18, risk: 'medium', retirements: 8, pipeline: 22 },
    { title: 'Licensed Store Manager', gap: 220, risk: 'medium', retirements: 45, pipeline: 165 },
  ],
  topPrograms: [
    {
      name: 'Starbucks College Achievement (ASU)',
      provider: 'Arizona State University',
      type: 'Degree',
      duration: '48 months',
      cost: '$0 (100% covered)',
      completionRate: 68,
      roi: 520,
      enrollments: 28000,
    },
    {
      name: 'Store Manager Development',
      provider: 'Internal',
      type: 'Development',
      duration: '16 weeks',
      cost: '$3,200',
      completionRate: 88,
      roi: 385,
      enrollments: 4200,
    },
    {
      name: 'Shift Supervisor Readiness',
      provider: 'Internal',
      type: 'Development',
      duration: '8 weeks',
      cost: '$1,500',
      completionRate: 92,
      roi: 290,
      enrollments: 8500,
    },
    {
      name: 'District Manager Academy',
      provider: 'Internal',
      type: 'Leadership',
      duration: '12 weeks',
      cost: '$8,000',
      completionRate: 95,
      roi: 445,
      enrollments: 320,
    },
    {
      name: 'Food Safety Certification',
      provider: 'ServSafe',
      type: 'Certificate',
      duration: '1 day',
      cost: '$180',
      completionRate: 98,
      roi: 150,
      enrollments: 52000,
    },
  ],
  pipeline: {
    identified: 42000,
    inProgram: 28500,
    developing: 8200,
    readyNow: 2800,
    promoted: 2150,
    attrition: 4800,
  },
  competencies: [
    { name: 'Customer Connection', avgScore: 88, target: 95, gap: 7 },
    { name: 'Beverage Quality', avgScore: 92, target: 95, gap: 3 },
    { name: 'Team Development', avgScore: 78, target: 88, gap: 10 },
    { name: 'Store Operations', avgScore: 82, target: 90, gap: 8 },
    { name: 'Financial Management', avgScore: 65, target: 80, gap: 15 },
    { name: 'Community Building', avgScore: 85, target: 90, gap: 5 },
  ],
  departments: [
    { name: 'Retail Stores', employees: 340000, pipeline: 38000 },
    { name: 'Licensed Stores', employees: 35000, pipeline: 2200 },
    { name: 'Roasting & Manufacturing', employees: 8000, pipeline: 180 },
    { name: 'Supply Chain', employees: 12000, pipeline: 280 },
    { name: 'Corporate Support', employees: 7000, pipeline: 140 },
  ],
};

const STATE_OF_DELAWARE: DemoClientData = {
  client: {
    id: 'delaware',
    name: 'State of Delaware',
    industry: 'Public Sector',
    employeeCount: 30000,
    taUtilization: 21,
    planActivation: 32,
    completionRate: 81,
    internalMobility: 19,
  },
  criticalRoles: [
    {
      title: 'Social Services Specialist',
      gap: 185,
      risk: 'critical',
      retirements: 48,
      pipeline: 96,
    },
    { title: 'Corrections Officer', gap: 260, risk: 'critical', retirements: 72, pipeline: 125 },
    { title: 'IT Security Analyst', gap: 42, risk: 'high', retirements: 8, pipeline: 24 },
    { title: 'Public Health Nurse', gap: 58, risk: 'high', retirements: 18, pipeline: 31 },
    { title: 'Program Manager', gap: 36, risk: 'medium', retirements: 16, pipeline: 22 },
  ],
  topPrograms: [
    {
      name: 'Public Administration Certificate',
      provider: 'University of Delaware',
      type: 'Certificate',
      duration: '6 months',
      cost: '$3,600',
      completionRate: 87,
      roi: 205,
      enrollments: 230,
    },
    {
      name: 'Cybersecurity Fundamentals',
      provider: 'Wilmington University',
      type: 'Certificate',
      duration: '16 weeks',
      cost: '$2,400',
      completionRate: 83,
      roi: 260,
      enrollments: 150,
    },
    {
      name: 'Human Services Leadership',
      provider: 'Internal',
      type: 'Development',
      duration: '10 weeks',
      cost: '$1,500',
      completionRate: 91,
      roi: 215,
      enrollments: 310,
    },
    {
      name: 'Criminal Justice Administration',
      provider: 'Delaware Technical Community College',
      type: 'Degree',
      duration: '24 months',
      cost: '$9,800',
      completionRate: 75,
      roi: 185,
      enrollments: 170,
    },
    {
      name: 'Public Health Practice',
      provider: 'Johns Hopkins Bloomberg School',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$2,200',
      completionRate: 86,
      roi: 195,
      enrollments: 90,
    },
  ],
  pipeline: {
    identified: 1250,
    inProgram: 760,
    developing: 280,
    readyNow: 105,
    promoted: 74,
    attrition: 118,
  },
  competencies: [
    { name: 'Constituent Service', avgScore: 84, target: 92, gap: 8 },
    { name: 'Policy Execution', avgScore: 76, target: 88, gap: 12 },
    { name: 'Digital Government', avgScore: 68, target: 84, gap: 16 },
    { name: 'Program Management', avgScore: 72, target: 86, gap: 14 },
    { name: 'Compliance', avgScore: 82, target: 92, gap: 10 },
  ],
  departments: [
    { name: 'Health & Social Services', employees: 9200, pipeline: 330 },
    { name: 'Correction', employees: 4800, pipeline: 240 },
    { name: 'Education', employees: 4100, pipeline: 115 },
    { name: 'Technology & Information', employees: 1400, pipeline: 75 },
    { name: 'Administration', employees: 10500, pipeline: 160 },
  ],
};

const UNIVERSITY_OF_MIAMI: DemoClientData = {
  client: {
    id: 'university-of-miami',
    name: 'University of Miami',
    industry: 'Higher Education & Healthcare',
    employeeCount: 19000,
    taUtilization: 34,
    planActivation: 41,
    completionRate: 83,
    internalMobility: 24,
  },
  criticalRoles: [
    {
      title: 'Clinical Research Coordinator',
      gap: 74,
      risk: 'high',
      retirements: 14,
      pipeline: 42,
    },
    { title: 'Registered Nurse (RN)', gap: 220, risk: 'critical', retirements: 56, pipeline: 118 },
    { title: 'Student Success Advisor', gap: 38, risk: 'medium', retirements: 10, pipeline: 26 },
    { title: 'Research Administrator', gap: 45, risk: 'high', retirements: 12, pipeline: 22 },
    { title: 'IT Systems Analyst', gap: 32, risk: 'medium', retirements: 7, pipeline: 20 },
  ],
  topPrograms: [
    {
      name: 'Healthcare MBA',
      provider: 'University of Miami Patti and Allan Herbert Business School',
      type: 'Graduate',
      duration: '24 months',
      cost: '$48,000',
      completionRate: 79,
      roi: 190,
      enrollments: 86,
    },
    {
      name: 'Clinical Research Operations',
      provider: 'Internal',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$2,800',
      completionRate: 88,
      roi: 235,
      enrollments: 140,
    },
    {
      name: 'Higher Ed Leadership',
      provider: 'ACE',
      type: 'Development',
      duration: '8 weeks',
      cost: '$2,200',
      completionRate: 91,
      roi: 215,
      enrollments: 120,
    },
    {
      name: 'RN-to-BSN Completion',
      provider: 'University of Miami School of Nursing',
      type: 'Degree',
      duration: '18 months',
      cost: '$14,500',
      completionRate: 82,
      roi: 230,
      enrollments: 210,
    },
    {
      name: 'Research Administration Certificate',
      provider: 'SRA International',
      type: 'Certificate',
      duration: '10 weeks',
      cost: '$1,900',
      completionRate: 86,
      roi: 205,
      enrollments: 95,
    },
  ],
  pipeline: {
    identified: 920,
    inProgram: 610,
    developing: 210,
    readyNow: 92,
    promoted: 63,
    attrition: 84,
  },
  competencies: [
    { name: 'Research Operations', avgScore: 78, target: 90, gap: 12 },
    { name: 'Student Support', avgScore: 84, target: 92, gap: 8 },
    { name: 'Clinical Quality', avgScore: 82, target: 92, gap: 10 },
    { name: 'Data Literacy', avgScore: 70, target: 86, gap: 16 },
    { name: 'Inclusive Leadership', avgScore: 80, target: 90, gap: 10 },
  ],
  departments: [
    { name: 'UHealth', employees: 8200, pipeline: 310 },
    { name: 'Academic Affairs', employees: 3900, pipeline: 135 },
    { name: 'Research', employees: 2400, pipeline: 110 },
    { name: 'Student Life', employees: 1700, pipeline: 70 },
    { name: 'Administration', employees: 2800, pipeline: 85 },
  ],
};

const SHELBY_COUNTY_PUBLIC_SCHOOLS: DemoClientData = {
  client: {
    id: 'shelby-county',
    name: 'Shelby County Public Schools',
    industry: 'K-12 Education',
    employeeCount: 14000,
    taUtilization: 29,
    planActivation: 36,
    completionRate: 77,
    internalMobility: 18,
  },
  criticalRoles: [
    {
      title: 'Special Education Teacher',
      gap: 160,
      risk: 'critical',
      retirements: 42,
      pipeline: 68,
    },
    { title: 'STEM Teacher', gap: 135, risk: 'critical', retirements: 35, pipeline: 74 },
    { title: 'School Counselor', gap: 48, risk: 'high', retirements: 16, pipeline: 26 },
    { title: 'Assistant Principal', gap: 28, risk: 'medium', retirements: 12, pipeline: 21 },
    { title: 'Bilingual Educator', gap: 74, risk: 'high', retirements: 18, pipeline: 34 },
  ],
  topPrograms: [
    {
      name: 'Teacher Licensure Pathway',
      provider: 'University of Memphis',
      type: 'Degree',
      duration: '24 months',
      cost: '$12,000',
      completionRate: 74,
      roi: 215,
      enrollments: 320,
    },
    {
      name: 'Special Education Endorsement',
      provider: 'Tennessee State University',
      type: 'Certificate',
      duration: '9 months',
      cost: '$4,800',
      completionRate: 82,
      roi: 240,
      enrollments: 180,
    },
    {
      name: 'Instructional Leadership',
      provider: 'Internal',
      type: 'Development',
      duration: '12 weeks',
      cost: '$1,600',
      completionRate: 89,
      roi: 225,
      enrollments: 210,
    },
    {
      name: 'School Counseling Bridge',
      provider: 'Vanderbilt Peabody Online',
      type: 'Graduate',
      duration: '24 months',
      cost: '$32,000',
      completionRate: 76,
      roi: 175,
      enrollments: 60,
    },
    {
      name: 'Bilingual Education Certificate',
      provider: 'Regional Education Cooperative',
      type: 'Certificate',
      duration: '6 months',
      cost: '$2,900',
      completionRate: 84,
      roi: 210,
      enrollments: 115,
    },
  ],
  pipeline: {
    identified: 780,
    inProgram: 480,
    developing: 185,
    readyNow: 62,
    promoted: 44,
    attrition: 92,
  },
  competencies: [
    { name: 'Instructional Practice', avgScore: 80, target: 90, gap: 10 },
    { name: 'Student Support', avgScore: 82, target: 92, gap: 10 },
    { name: 'Classroom Technology', avgScore: 70, target: 86, gap: 16 },
    { name: 'Inclusive Education', avgScore: 76, target: 90, gap: 14 },
    { name: 'Leadership Readiness', avgScore: 68, target: 84, gap: 16 },
  ],
  departments: [
    { name: 'Elementary Schools', employees: 5200, pipeline: 210 },
    { name: 'Middle Schools', employees: 2600, pipeline: 115 },
    { name: 'High Schools', employees: 3300, pipeline: 125 },
    { name: 'Student Support', employees: 1500, pipeline: 70 },
    { name: 'District Operations', employees: 1400, pipeline: 45 },
  ],
};

const VERIZON_DATA: DemoClientData = {
  client: {
    id: 'verizon',
    name: 'Verizon',
    industry: 'Telecommunications',
    employeeCount: 105000,
    taUtilization: 24,
    planActivation: 44,
    completionRate: 80,
    internalMobility: 21,
  },
  criticalRoles: [
    { title: 'Network Engineer', gap: 420, risk: 'critical', retirements: 96, pipeline: 220 },
    { title: 'Cybersecurity Analyst', gap: 210, risk: 'high', retirements: 35, pipeline: 115 },
    { title: 'Retail Store Manager', gap: 310, risk: 'high', retirements: 78, pipeline: 185 },
    { title: 'Cloud Solutions Architect', gap: 145, risk: 'high', retirements: 24, pipeline: 72 },
    { title: 'Customer Experience Lead', gap: 190, risk: 'medium', retirements: 55, pipeline: 120 },
  ],
  topPrograms: [
    {
      name: '5G Network Engineering',
      provider: 'Internal',
      type: 'Certificate',
      duration: '14 weeks',
      cost: '$3,800',
      completionRate: 86,
      roi: 310,
      enrollments: 950,
    },
    {
      name: 'Cybersecurity Professional',
      provider: 'SANS Institute',
      type: 'Certificate',
      duration: '16 weeks',
      cost: '$8,500',
      completionRate: 82,
      roi: 245,
      enrollments: 430,
    },
    {
      name: 'Cloud Architecture',
      provider: 'AWS Training',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$2,400',
      completionRate: 84,
      roi: 285,
      enrollments: 520,
    },
    {
      name: 'Retail Leadership Academy',
      provider: 'Internal',
      type: 'Development',
      duration: '10 weeks',
      cost: '$1,900',
      completionRate: 91,
      roi: 260,
      enrollments: 1200,
    },
    {
      name: 'Data Analytics for Telecom',
      provider: 'Coursera / Google',
      type: 'Certificate',
      duration: '6 months',
      cost: '$1,400',
      completionRate: 78,
      roi: 220,
      enrollments: 360,
    },
  ],
  pipeline: {
    identified: 4200,
    inProgram: 2550,
    developing: 930,
    readyNow: 310,
    promoted: 240,
    attrition: 380,
  },
  competencies: [
    { name: 'Network Architecture', avgScore: 78, target: 90, gap: 12 },
    { name: 'Security Operations', avgScore: 74, target: 88, gap: 14 },
    { name: 'Customer Experience', avgScore: 84, target: 92, gap: 8 },
    { name: 'Cloud Platforms', avgScore: 72, target: 86, gap: 14 },
    { name: 'Data Fluency', avgScore: 70, target: 84, gap: 14 },
  ],
  departments: [
    { name: 'Network & Technology', employees: 31000, pipeline: 1350 },
    { name: 'Consumer Retail', employees: 28000, pipeline: 960 },
    { name: 'Business Markets', employees: 18000, pipeline: 640 },
    { name: 'Security & Cloud', employees: 9500, pipeline: 410 },
    { name: 'Corporate Functions', employees: 18500, pipeline: 300 },
  ],
};

const LEGAL_FIRM_DATA: DemoClientData = {
  client: {
    id: 'legal',
    name: 'Morrison & Sterling LLP',
    industry: 'Legal Services',
    employeeCount: 4500,
    taUtilization: 32,
    planActivation: 48,
    completionRate: 89,
    internalMobility: 18,
  },
  criticalRoles: [
    { title: 'Associate Attorney', gap: 85, risk: 'critical', retirements: 12, pipeline: 45 },
    { title: 'Partner Track', gap: 22, risk: 'high', retirements: 8, pipeline: 15 },
    { title: 'Paralegal', gap: 45, risk: 'medium', retirements: 18, pipeline: 32 },
    { title: 'Legal Operations', gap: 12, risk: 'medium', retirements: 5, pipeline: 8 },
    { title: 'Practice Group Leader', gap: 8, risk: 'high', retirements: 6, pipeline: 4 },
  ],
  topPrograms: [
    {
      name: 'LL.M. in Tax Law',
      provider: 'NYU School of Law',
      type: 'Graduate',
      duration: '12 months',
      cost: '$68,000',
      completionRate: 95,
      roi: 285,
      enrollments: 24,
    },
    {
      name: 'Project Management for Legal',
      provider: 'ALSP Institute',
      type: 'Certificate',
      duration: '8 weeks',
      cost: '$2,500',
      completionRate: 92,
      roi: 320,
      enrollments: 156,
    },
    {
      name: 'Legal Technology Certificate',
      provider: 'Georgetown Law',
      type: 'Certificate',
      duration: '6 months',
      cost: '$8,500',
      completionRate: 88,
      roi: 195,
      enrollments: 68,
    },
    {
      name: 'Leadership for Lawyers',
      provider: 'Harvard Law School',
      type: 'Executive',
      duration: '2 weeks',
      cost: '$15,000',
      completionRate: 98,
      roi: 250,
      enrollments: 18,
    },
    {
      name: 'Paralegal Certificate',
      provider: 'ABA Approved Programs',
      type: 'Certificate',
      duration: '9 months',
      cost: '$12,000',
      completionRate: 85,
      roi: 180,
      enrollments: 42,
    },
  ],
  pipeline: {
    identified: 380,
    inProgram: 245,
    developing: 85,
    readyNow: 42,
    promoted: 28,
    attrition: 45,
  },
  competencies: [
    { name: 'Legal Research', avgScore: 92, target: 95, gap: 3 },
    { name: 'Client Relations', avgScore: 85, target: 92, gap: 7 },
    { name: 'Business Development', avgScore: 68, target: 85, gap: 17 },
    { name: 'Practice Management', avgScore: 72, target: 88, gap: 16 },
    { name: 'Technology Proficiency', avgScore: 65, target: 82, gap: 17 },
    { name: 'Leadership', avgScore: 78, target: 90, gap: 12 },
  ],
  departments: [
    { name: 'Corporate', employees: 1200, pipeline: 95 },
    { name: 'Litigation', employees: 1400, pipeline: 110 },
    { name: 'Tax & ERISA', employees: 450, pipeline: 35 },
    { name: 'Real Estate', employees: 380, pipeline: 28 },
    { name: 'Labor & Employment', employees: 520, pipeline: 42 },
    { name: 'Business Operations', employees: 550, pipeline: 25 },
  ],
};

const DAFFY_DATA: DemoClientData = {
  client: {
    id: 'daffy',
    name: 'Daffy Technologies',
    industry: 'Technology',
    employeeCount: 25000,
    taUtilization: 18,
    planActivation: 28,
    completionRate: 76,
    internalMobility: 15,
  },
  criticalRoles: [
    { title: 'Software Engineer', gap: 320, risk: 'critical', retirements: 45, pipeline: 180 },
    { title: 'Engineering Manager', gap: 42, risk: 'high', retirements: 18, pipeline: 28 },
    { title: 'Product Manager', gap: 65, risk: 'high', retirements: 12, pipeline: 35 },
    { title: 'Data Scientist', gap: 85, risk: 'medium', retirements: 8, pipeline: 45 },
    { title: 'UX Designer', gap: 38, risk: 'medium', retirements: 6, pipeline: 22 },
  ],
  topPrograms: [
    {
      name: 'AWS Solutions Architect',
      provider: 'Amazon Web Services',
      type: 'Certificate',
      duration: '12 weeks',
      cost: '$1,500',
      completionRate: 82,
      roi: 245,
      enrollments: 850,
    },
    {
      name: 'MBA - Technology Leadership',
      provider: 'Stanford GSB Online',
      type: 'Graduate',
      duration: '24 months',
      cost: '$75,000',
      completionRate: 78,
      roi: 185,
      enrollments: 45,
    },
    {
      name: 'Machine Learning Engineer',
      provider: 'Coursera / Google',
      type: 'Certificate',
      duration: '6 months',
      cost: '$1,200',
      completionRate: 72,
      roi: 210,
      enrollments: 420,
    },
    {
      name: 'Product Management',
      provider: 'Reforge',
      type: 'Certificate',
      duration: '8 weeks',
      cost: '$3,000',
      completionRate: 88,
      roi: 275,
      enrollments: 180,
    },
    {
      name: 'Cybersecurity Certificate',
      provider: 'SANS Institute',
      type: 'Certificate',
      duration: '16 weeks',
      cost: '$8,500',
      completionRate: 85,
      roi: 195,
      enrollments: 125,
    },
  ],
  pipeline: {
    identified: 2800,
    inProgram: 1650,
    developing: 580,
    readyNow: 245,
    promoted: 180,
    attrition: 320,
  },
  competencies: [
    { name: 'Technical Excellence', avgScore: 82, target: 92, gap: 10 },
    { name: 'Cloud Architecture', avgScore: 75, target: 88, gap: 13 },
    { name: 'Data & Analytics', avgScore: 72, target: 85, gap: 13 },
    { name: 'Leadership', avgScore: 78, target: 88, gap: 10 },
    { name: 'Product Thinking', avgScore: 70, target: 85, gap: 15 },
    { name: 'Security Awareness', avgScore: 68, target: 82, gap: 14 },
  ],
  departments: [
    { name: 'Engineering', employees: 12000, pipeline: 1200 },
    { name: 'Product', employees: 3500, pipeline: 280 },
    { name: 'Data Science', employees: 2200, pipeline: 180 },
    { name: 'Design', employees: 1800, pipeline: 120 },
    { name: 'Operations', employees: 3200, pipeline: 150 },
    { name: 'Corporate', employees: 2300, pipeline: 85 },
  ],
};

// Non-profit / government education persona. Reuses State of Delaware's
// public-sector career-pathways dataset under the Colorado identity so the
// admin/career surfaces stay coherent without fabricating a parallel dataset.
const COLORADO_HIGHER_ED: DemoClientData = {
  ...STATE_OF_DELAWARE,
  client: {
    ...STATE_OF_DELAWARE.client,
    id: 'colorado',
    name: 'Colorado Department of Higher Education',
    industry: 'Government & Education',
    personaLabel: 'Non-profit — government / education',
  },
};

export const DEMO_CLIENTS: Record<DemoClientId, DemoClientData> = {
  lifepoint: LIFEPOINT_HEALTH,
  advocate: ADVOCATE_HEALTH,
  colorado: COLORADO_HIGHER_ED,
  starbucks: STARBUCKS_DATA,
  delaware: STATE_OF_DELAWARE,
  'university-of-miami': UNIVERSITY_OF_MIAMI,
  'shelby-county': SHELBY_COUNTY_PUBLIC_SCHOOLS,
  verizon: VERIZON_DATA,
  ups: UPS_DATA,
  daffy: DAFFY_DATA,
  legal: LEGAL_FIRM_DATA,
};

// The curated "Persona" picker: four employer archetypes the sales team demos
// (for-profit health, non-profit health, non-profit gov/education, for-profit
// other). The other clients stay in DEMO_CLIENTS — reachable via the
// `demo_client` URL param — but off the menu.
export const DEMO_CLIENT_LIST: DemoClient[] = [
  LIFEPOINT_HEALTH.client,
  ADVOCATE_HEALTH.client,
  COLORADO_HIGHER_ED.client,
  STARBUCKS_DATA.client,
];

/**
 * Employer tax/profit status per client — the single source of truth for PSLF
 * eligibility (consumed by isPslfEligibleClient, personaMaskedFeatures, and the
 * company-settings employer block).
 */
const CLIENT_EMPLOYER_TYPE: Record<DemoClientId, DemoEmployerType> = {
  lifepoint: 'for-profit',
  advocate: 'non-profit',
  colorado: 'government',
  starbucks: 'for-profit',
  delaware: 'government',
  'university-of-miami': 'non-profit',
  'shelby-county': 'government',
  verizon: 'for-profit',
  ups: 'for-profit',
  daffy: 'for-profit',
  legal: 'for-profit',
};

export function getDemoEmployerType(clientId: DemoClientId): DemoEmployerType {
  return CLIENT_EMPLOYER_TYPE[clientId];
}

/**
 * PSLF qualifies non-profit + government employment, never for-profit. This is
 * the real-world gate the persona axis makes responsive.
 */
export function isPslfEligibleClient(clientId: DemoClientId): boolean {
  return CLIENT_EMPLOYER_TYPE[clientId] !== 'for-profit';
}

// The ONE source of truth for the demo's tuition-assistance programs, consumed
// by all three demo planes so they can never drift: the dashboard's eligible-
// program cards (reimagined-data.getDemoEligibleTemplates), the getting-started
// route's demo model (buildDemoModel), and the store/fetch fixtures
// (demo/products/tas-fixtures.ts). Template ids ARE the store-plane ids.
// Program 3 is industry-aware (sales: RN→BSN for healthcare flows, IC→Management
// otherwise), keyed off the demo client's `industry`.

// A `type` alias (NOT an interface) on purpose: warp-drive's store.push
// constrains attribute values to its JSON `Value` type, and only type-alias
// object literals carry the implicit index signature that satisfies it.
export type DemoTaProgramFaq = {
  /** Required — the FAQ component sorts on it. */
  ordinal: number;
  heading: string;
  body: string;
};

export interface DemoTaProgram {
  /** tas-program-template id, shared across every demo plane. */
  id: string;
  /** Eligibility-map key: /dynamic-eligibility + /periods-of-eligibility are keyed by template code. */
  code: string;
  name: string;
  description: string;
  /** CourseCa$h™ (direct-pay) program — drives the card chip + template payment fields. */
  isDirectPayProgram: boolean;
  typeClassification: 'TAS.ProgramType.4.2';
  faqs: DemoTaProgramFaq[];
}

/** The walkthrough program: keep this id — the seeded draft + submitted
 * application, approval queue, and admin drill-ins all hang off it. */
export const DEMO_TA_PRIMARY_TEMPLATE_ID = 'demo-tas-template-1';

const TUITION_REIMBURSEMENT_PROGRAM: DemoTaProgram = {
  id: DEMO_TA_PRIMARY_TEMPLATE_ID,
  code: 'DEMO_PROGRAM',
  name: 'Tuition Reimbursement',
  description:
    'Take coursework toward a degree at an accredited institution and get reimbursed after grades post.',
  isDirectPayProgram: false,
  typeClassification: 'TAS.ProgramType.4.2',
  faqs: [
    {
      ordinal: 1,
      heading: 'Who is eligible?',
      body: 'Full-time employees are eligible after 90 days of service. Part-time eligibility varies by role — check your plan details.',
    },
    {
      ordinal: 2,
      heading: 'How much is covered?',
      body: 'Up to $5,250 per calendar year in tuition, books, and fees — the IRS tax-free education benefit limit.',
    },
    {
      ordinal: 3,
      heading: 'When do I get reimbursed?',
      body: 'Submit your grades and receipts after the course ends; reimbursement is issued with a regular payroll cycle after approval.',
    },
  ],
};

const CERTS_PROGRAM: DemoTaProgram = {
  id: 'demo-tas-template-2',
  code: 'DEMO_CERTS',
  name: 'Certs, CEUs & CMEs',
  description:
    'Keep your certifications and licenses current — exam fees, prep courses, and continuing-education credits.',
  isDirectPayProgram: false,
  typeClassification: 'TAS.ProgramType.4.2',
  faqs: [
    {
      ordinal: 1,
      heading: 'What expenses qualify?',
      body: 'Exam and registration fees, prep materials, renewal fees, and accredited CEU/CME coursework required for your role.',
    },
    {
      ordinal: 2,
      heading: 'Do I need pre-approval?',
      body: 'Yes — submit the certification or course for approval before you register so the expense is guaranteed to qualify.',
    },
    {
      ordinal: 3,
      heading: 'How do CEU/CME hours get reported?',
      body: 'Upload your completion certificate; hours are recorded with your application for compliance reporting.',
    },
  ],
};

const RN_TO_BSN_PROGRAM: DemoTaProgram = {
  id: 'demo-tas-template-3',
  code: 'DEMO_DEGREE',
  name: 'RN to BSN',
  description:
    'Advance from RN to BSN through accredited nursing-school partners, with coursework scheduled around clinical shifts.',
  isDirectPayProgram: false,
  typeClassification: 'TAS.ProgramType.4.2',
  faqs: [
    {
      ordinal: 1,
      heading: 'Which schools can I attend?',
      body: 'Any CCNE- or ACEN-accredited BSN program qualifies; Learning Partner Network schools offer additional tuition discounts.',
    },
    {
      ordinal: 2,
      heading: 'Will this work with my clinical schedule?',
      body: 'Partner programs are designed for working nurses — most coursework is online with flexible clinical placement.',
    },
    {
      ordinal: 3,
      heading: 'What does the benefit cover?',
      body: 'Tuition, required textbooks, and program fees up to your annual plan limit.',
    },
  ],
};

const IC_TO_MANAGEMENT_PROGRAM: DemoTaProgram = {
  id: 'demo-tas-template-3',
  code: 'DEMO_DEGREE',
  name: 'IC to Management',
  description:
    'Build the leadership skills to move from individual contributor into people management — coursework, workshops, and coaching.',
  isDirectPayProgram: false,
  typeClassification: 'TAS.ProgramType.4.2',
  faqs: [
    {
      ordinal: 1,
      heading: 'Who is this program for?',
      body: 'High-performing individual contributors preparing for a first people-management role, with their manager’s endorsement.',
    },
    {
      ordinal: 2,
      heading: 'What does it include?',
      body: 'Leadership coursework from accredited providers, management workshops, and a structured coaching track.',
    },
    {
      ordinal: 3,
      heading: 'Does my manager need to approve?',
      body: 'Yes — applications route through your manager as part of the standard approval chain.',
    },
  ],
};

const PREPAID_PROGRAM: DemoTaProgram = {
  id: 'demo-tas-template-4',
  code: 'DEMO_PREPAID',
  name: 'Prepaid Programs',
  description:
    'Pre-approved courses paid up front through CourseCa$h™ — no out-of-pocket cost and no waiting on reimbursement.',
  isDirectPayProgram: true,
  typeClassification: 'TAS.ProgramType.4.2',
  faqs: [
    {
      ordinal: 1,
      heading: 'How does prepayment work?',
      body: 'Once your application is approved, tuition is paid directly to the provider through CourseCa$h™ — you never pay out of pocket.',
    },
    {
      ordinal: 2,
      heading: 'Where can I use CourseCa$h™?',
      body: 'At any provider in the pre-approved catalog; your application shows the covered amount before you enroll.',
    },
    {
      ordinal: 3,
      heading: 'What happens to unused funds?',
      body: 'Unused CourseCa$h™ is released back to your annual benefit — it never counts against your limit.',
    },
  ],
};

/**
 * The four demo TA programs for a client: Tuition Reimbursement, Certs/CEUs &
 * CMEs, an industry-aware degree/leadership program (RN→BSN for healthcare
 * clients, IC→Management otherwise), and Prepaid (CourseCa$h™).
 */
export function getDemoTaProgramsForClient(clientId: DemoClientId): DemoTaProgram[] {
  const isHealthcare = DEMO_CLIENTS[clientId]?.client.industry?.includes('Healthcare') ?? false;
  return [
    TUITION_REIMBURSEMENT_PROGRAM,
    CERTS_PROGRAM,
    isHealthcare ? RN_TO_BSN_PROGRAM : IC_TO_MANAGEMENT_PROGRAM,
    PREPAID_PROGRAM,
  ];
}

/**
 * Every catalog template code — CLIENT-INDEPENDENT (the industry-aware slot's
 * two variants share one id/code). The eligibility maps
 * (/dynamic-eligibility, /periods-of-eligibility) are keyed by these; a
 * template whose code is missing from either map is filtered off the
 * dashboard.
 */
export const DEMO_TA_PROGRAM_CODES: string[] = [
  ...new Set(
    [TUITION_REIMBURSEMENT_PROGRAM, CERTS_PROGRAM, RN_TO_BSN_PROGRAM, PREPAID_PROGRAM].map(
      (program) => program.code
    )
  ),
];

export function getDemoData(clientId: DemoClientId): DemoClientData {
  return DEMO_CLIENTS[clientId];
}

export function getDemoClientIdFromUrl(): DemoClientId {
  if (typeof window === 'undefined') {
    return 'advocate';
  }

  const params = new URLSearchParams(window.location.search);
  const client = params.get('demo_client') as DemoClientId | null;
  if (client && hasOwn(DEMO_CLIENTS, client)) {
    localStorage.setItem('reimagined-demo-client', client);
    return client;
  }

  // Fall back to localStorage if URL param is missing
  const stored = localStorage.getItem('reimagined-demo-client') as DemoClientId | null;
  if (stored && hasOwn(DEMO_CLIENTS, stored)) {
    return stored;
  }

  return 'advocate';
}

export function getCurrentDemoClientId(): DemoClientId {
  return getDemoClientIdFromUrl();
}

export function getCurrentDemoClientData(): DemoClientData {
  return getDemoData(getDemoClientIdFromUrl());
}

const DEMO_CLIENT_LOGO_COLORS: Record<DemoClientId, string> = {
  lifepoint: '#6d28d9',
  advocate: '#0f766e',
  colorado: '#b91c1c',
  starbucks: '#006241',
  delaware: '#1d4ed8',
  'university-of-miami': '#f97316',
  'shelby-county': '#7c3aed',
  verizon: '#dc2626',
  ups: '#7c2d12',
  daffy: '#2563eb',
  legal: '#374151',
};

function escapeSvgText(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&apos;';
      default:
        return char;
    }
  });
}

function getClientInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

export function getDemoClientLogoUrl(clientId: DemoClientId): string {
  const client = getDemoData(clientId).client;
  if (client.logo) {
    return client.logo;
  }

  const color = DEMO_CLIENT_LOGO_COLORS[client.id];
  const initials = escapeSvgText(getClientInitials(client.name));
  const name = escapeSvgText(client.name);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="96" viewBox="0 0 320 96" role="img" aria-label="${name}"><rect width="320" height="96" rx="14" fill="white"/><circle cx="48" cy="48" r="30" fill="${color}"/><text x="48" y="58" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="24" font-weight="700" fill="white">${initials}</text><text x="94" y="57" font-family="Arial, Helvetica, sans-serif" font-size="27" font-weight="700" fill="${color}">${name}</text></svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

export function getCurrentDemoClientLogoUrl(): string {
  return getDemoClientLogoUrl(getCurrentDemoClientId());
}

export function getDemoScenario(scenarioId: DemoScenarioId): DemoScenario {
  return DEMO_SCENARIOS[scenarioId];
}

export function getDemoScenarioIdFromUrl(): DemoScenarioId {
  if (typeof window === 'undefined') {
    return 'full';
  }

  const params = new URLSearchParams(window.location.search);

  // Normalize on read: when the URL carries a demo_product that decodes to a
  // real benefit set, the PRODUCTS are the axis of record — derive the base
  // scenario from them and ignore a conflicting explicit demo_scenario.
  // Canonical links (buildDemoSessionUrl) emit both consistently, so this only
  // bites hand-edited/stale URLs, where honoring the scenario over the
  // products made the nav disagree with page content (nav keyed on the
  // scenario, features on the products). A GARBAGE product param (e.g. a
  // prototype-pollution probe) decodes empty and must not hijack the axis —
  // it falls through to the explicit scenario below.
  const productParam = params.get('demo_product');
  if (productParam) {
    const paramIds = normalizeProductIds(productParam.split(',').flatMap(decodeProductToken));
    if (paramIds.length) {
      const derived = resolveBaseScenarioId(paramIds);
      if (localStorage.getItem('reimagined-demo-scenario') !== derived) {
        localStorage.setItem('reimagined-demo-scenario', derived);
      }
      return derived;
    }
  }

  // A scenario-only link (legacy deep links, the audit's seeded cells) is
  // honored when no valid product param contradicts it.
  const scenario = params.get('demo_scenario') as DemoScenarioId | null;
  if (scenario && hasOwn(DEMO_SCENARIOS, scenario)) {
    // Persist to localStorage so it survives page reloads
    localStorage.setItem('reimagined-demo-scenario', scenario);
    return scenario;
  }

  // Fall back to localStorage if no URL params are present (e.g. after reload)
  const stored = localStorage.getItem('reimagined-demo-scenario') as DemoScenarioId | null;
  if (stored && hasOwn(DEMO_SCENARIOS, stored)) {
    return stored;
  }

  // No explicit scenario or product: derive the base from the stored/default
  // product set so the banner dropdown and the mega-menu nav (which key on a
  // scenario id) reflect the products instead of always defaulting to 'full'.
  return resolveBaseScenarioId(getDemoProductIdsFromUrl());
}

/**
 * Base scenario id for a selected benefit set — supplies the composite's
 * userState + messaging AND the `demo_scenario` the nav/banner read (the nav
 * hides items via hideFor keyed on 'ta-only' / 'slrp-only' / 'no-ta'). Exact
 * feature-set matches keep canonical/legacy sets on their own base; genuinely
 * novel atomic combinations fall back to the closest nav-correct base.
 */
function resolveBaseScenarioId(productIds: DemoProductId[]): DemoScenarioId {
  // All six benefits → the canonical full session.
  if (productIds.length === DEMO_PRODUCT_IDS.length) {
    return 'full';
  }
  const has = (key: DemoProductId) => productIds.includes(key);
  // Exact feature-set match (best userState/messaging + nav fidelity).
  const exact = Object.values(DEMO_SCENARIOS).find((candidate) =>
    DEMO_PRODUCT_IDS.every((key) => candidate.features[key] === has(key))
  );
  if (exact) {
    return exact.id;
  }
  // Novel combination: pick the closest nav-correct base.
  const hasTa = has('tuitionAssistance');
  const hasLoanBenefit = has('studentLoanRepayment') || has('pslf') || has('employerMatch');
  if (hasTa && !hasLoanBenefit) {
    return 'ta-only';
  }
  if (!hasTa && hasLoanBenefit) {
    return has('careerPathways') ? 'no-ta' : 'slrp-only';
  }
  return 'full';
}

// Benefits a persona forbids regardless of product selection. PR1: none. The
// for-profit-persona PR returns a set containing 'pslf' for for-profit employers
// so the composite union can never enable PSLF for them.
const EMPTY_FEATURE_MASK: ReadonlySet<keyof DemoScenario['features']> = new Set();
const PSLF_FEATURE_MASK: ReadonlySet<keyof DemoScenario['features']> = new Set(['pslf']);

/**
 * Benefits the selected persona forbids regardless of the product selection. A
 * for-profit employer is not PSLF-eligible, so `pslf` is masked out of the
 * composite union — a for-profit + PSLF combination can never render (the
 * picker also disables the PSLF product for these personas; this is the
 * data-layer backstop for hand-edited URLs / stored sessions).
 */
function personaMaskedFeatures(
  clientId: DemoClientId
): ReadonlySet<keyof DemoScenario['features']> {
  return isPslfEligibleClient(clientId) ? EMPTY_FEATURE_MASK : PSLF_FEATURE_MASK;
}

/** `features` is the selection's benefits minus the persona-masked ones. */
export function demoScenarioForSelection(
  selection: Pick<DemoSessionSelection, 'clientId' | 'productIds'>
): DemoScenario {
  const normalized = normalizeProductIds(selection.productIds);
  // An all-false scenario would hide every surface.
  const productIds = normalized.length ? normalized : [...DEMO_PRODUCT_IDS];
  const masked = personaMaskedFeatures(selection.clientId);
  const features = Object.fromEntries(
    DEMO_PRODUCT_IDS.map((key) => [key, productIds.includes(key) && !masked.has(key)])
  ) as DemoScenario['features'];
  const baseId = resolveBaseScenarioId(productIds);
  const base = getDemoScenario(baseId);
  return {
    id: baseId,
    name: base.name,
    description: base.description,
    features,
    userState: base.userState,
    messaging: base.messaging,
  };
}

/** {@link demoScenarioForSelection} for whatever the browser has stored. */
export function getCurrentDemoScenario(): DemoScenario {
  return demoScenarioForSelection({
    clientId: getCurrentDemoClientId(),
    productIds: getCurrentDemoProductIds(),
  });
}

export interface DemoTasScenarioOption {
  id: DemoTasScenarioId;
  name: string;
  description: string;
}

// Single source for the TA-scenario axis: ids, picker order, and card copy.
// (Descriptions narrate the employee DASHBOARD; store-plane TA routes keep the walkthrough fixtures.)
export const DEMO_TAS_SCENARIOS: Record<DemoTasScenarioId, DemoTasScenarioOption> = {
  'not-started': {
    id: 'not-started',
    name: 'Not Started',
    description: 'No TA activity yet — the employee only sees the eligible-programs catalog',
  },
  'in-progress': {
    id: 'in-progress',
    name: 'In Progress',
    description:
      'One application approved and partly reimbursed, one awaiting approval (the walkthrough default)',
  },
  approved: {
    id: 'approved',
    name: 'Approved',
    description:
      'The submitted request approved in full — picks up right after the approval walkthrough',
  },
  rejected: {
    id: 'rejected',
    name: 'Rejected',
    description: 'The request came back denied — the employee sees the rejected state',
  },
  completed: {
    id: 'completed',
    name: 'Completed',
    description: 'Program finished — the application fulfilled and fully reimbursed',
  },
};

export const DEMO_TAS_SCENARIO_LIST = Object.keys(DEMO_TAS_SCENARIOS) as DemoTasScenarioId[];

export const DEMO_TAS_SCENARIO_OPTION_LIST = Object.values(DEMO_TAS_SCENARIOS);

export function getDemoTasScenarioOption(tasScenarioId: DemoTasScenarioId): DemoTasScenarioOption {
  return DEMO_TAS_SCENARIOS[tasScenarioId];
}

export function getDemoTasScenarioIdFromUrl(): DemoTasScenarioId | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const scenario = params.get('demo_ta_scenario') as DemoTasScenarioId | null;
  if (scenario && DEMO_TAS_SCENARIO_LIST.includes(scenario)) {
    localStorage.setItem('reimagined-demo-ta-scenario', scenario);
    return scenario;
  }

  // Fall back to localStorage if URL param is missing
  const stored = localStorage.getItem('reimagined-demo-ta-scenario') as DemoTasScenarioId | null;
  if (stored && DEMO_TAS_SCENARIO_LIST.includes(stored)) {
    return stored;
  }

  return null;
}

export function getCurrentDemoTasScenarioId(): DemoTasScenarioId {
  const urlScenario = getDemoTasScenarioIdFromUrl();
  if (urlScenario) {
    return urlScenario;
  }

  const scenario = getCurrentDemoScenario();
  if (!scenario.features.tuitionAssistance) {
    return 'not-started';
  }

  const userState = getDemoUserState();
  const taScenario = userState.taScenario;
  if (taScenario) {
    return taScenario;
  }

  if (userState.benefitsMaxedOut) {
    return 'completed';
  }

  if (userState.hasActiveTAApplication) {
    return 'in-progress';
  }

  return 'not-started';
}

// NOTE: the single-axis setters that used to live here (setDemoClientId /
// setDemoScenarioId / setDemoTasScenarioId) were the legacy header strip's
// write path and are gone with it. They wrote ONE axis (scenario) without
// reconciling the others (products), which let the nav disagree with page
// content. Every session write now goes through applyDemoSession /
// resolveDemoSessionAxes — one data model, no partial writes.

/**
 * Check if a specific feature is enabled in the current scenario
 */
export function isFeatureEnabled(feature: keyof DemoScenario['features']): boolean {
  return getCurrentDemoScenario().features[feature];
}

/**
 * Get current user state. When a user-persona scenario is selected via the
 * `demo_user_scenario` toggle, its `userState` overrides the product-derived
 * scenario so the persona toggle actually changes onboarding/usage state
 * without disturbing product feature gates.
 */
export function getDemoUserState(): DemoScenario['userState'] {
  const personaId = getCurrentDemoUserScenarioId();
  const persona = DEMO_USER_SCENARIOS[personaId];
  const personaScenario = persona ? DEMO_SCENARIOS[persona.scenarioId] : undefined;
  const base = personaScenario?.userState ?? getCurrentDemoScenario().userState;
  // The Debt axis owns linked-loan state — override whatever the base/persona
  // scenario declared. (The reimagined loans page reads the debt level directly;
  // this keeps every other userState consumer consistent.)
  return { ...base, hasLinkedLoans: getCurrentDemoDebtLevel().hasLinkedLoans };
}

/**
 * Get current messaging. Mirrors `getDemoUserState`: the user-persona scenario
 * wins for messaging when set, otherwise the product scenario provides it.
 */
export function getDemoMessaging(): DemoScenario['messaging'] {
  const personaId = getCurrentDemoUserScenarioId();
  const persona = DEMO_USER_SCENARIOS[personaId];
  const personaScenario = persona ? DEMO_SCENARIOS[persona.scenarioId] : undefined;
  return personaScenario?.messaging ?? getCurrentDemoScenario().messaging;
}

export const DEMO_PSLF_SCENARIO_LIST: DemoPslfScenarioId[] = [
  'blank-slate',
  'borrower-signed',
  'employer-signed',
  'submitted',
  'form-rejected',
  'multi-form',
  'with-nslds',
  'full-journey',
];

export function getDemoPslfScenarioIdFromUrl(): DemoPslfScenarioId | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const scenario = params.get('demo_pslf_scenario') as DemoPslfScenarioId | null;
  if (scenario && DEMO_PSLF_SCENARIO_LIST.includes(scenario)) {
    localStorage.setItem('reimagined-demo-pslf-scenario', scenario);
    return scenario;
  }

  // Fall back to localStorage if URL param is missing
  const stored = localStorage.getItem('reimagined-demo-pslf-scenario') as DemoPslfScenarioId | null;
  if (stored && DEMO_PSLF_SCENARIO_LIST.includes(stored)) {
    return stored;
  }

  return null;
}

export function getCurrentDemoPslfScenarioId(): DemoPslfScenarioId {
  return getDemoPslfScenarioIdFromUrl() ?? DEMO_PRODUCTS.pslf.defaultPslfScenarioId;
}

export function getDemoProduct(productId: DemoProductId): DemoProduct {
  return DEMO_PRODUCTS[productId];
}

export function getDemoSessionRole(roleId: DemoSessionRoleId): DemoSessionRole {
  return DEMO_SESSION_ROLES[roleId];
}

/** Picked role first: it is `sessionContext.currentRole`. The rest only widen what is visible. */
export function getDemoSessionRelationshipTypes(
  selection: Pick<DemoSessionSelection, 'clientId' | 'productIds' | 'roleId'>
): [string, ...string[]] {
  const types: [string, ...string[]] = [getDemoSessionRole(selection.roleId).relationshipType];

  // The Administration nav rows gate on this or TIO_ADMIN, not EMPLOYEE_ADMIN.
  if (selection.roleId === 'admin') {
    types.push('ACCOUNT_OWNER');
  }

  // A for-profit employer cannot qualify, so it administers nothing.
  const administersPslf =
    selection.roleId === 'admin' &&
    normalizeProductIds(selection.productIds).includes('pslf') &&
    isPslfEligibleClient(selection.clientId);

  if (administersPslf) {
    types.push('PSLF_ADMIN');
  }

  return types;
}

/** The demo plan's annual TA limit, in DOLLARS — the single ceiling every demo plane reports. */
export const DEMO_TAS_ANNUAL_LIMIT_DOLLARS = 5250;

/** Maxed-out demos pin each authored NONZERO amount to the limit; zeros/absents keep their meaning. */
export function pinToAnnualLimitIfNonzero<T extends number | null | undefined>(
  amount: T,
  limit: number
) {
  return amount ? limit : amount;
}

/** The persona's benefitsMaxedOut flag from the tables alone — no storage read. */
export function benefitsMaxedOutForPersona(userScenarioId: DemoUserScenarioId | undefined) {
  const persona = userScenarioId ? DEMO_USER_SCENARIOS[userScenarioId] : undefined;
  const scenario = persona ? DEMO_SCENARIOS[persona.scenarioId] : undefined;
  return scenario?.userState.benefitsMaxedOut ?? false;
}

export function getDemoUserScenario(scenarioId: DemoUserScenarioId): DemoUserScenario {
  return DEMO_USER_SCENARIOS[scenarioId];
}

export function getDemoTaPaymentModel(modelId: DemoTaPaymentModelId): DemoTaPaymentModel {
  return DEMO_TA_PAYMENT_MODELS[modelId];
}

/**
 * Whether the product's scenario enables Tuition Assistance. The TA payment
 * model configures TA-only behavior — the assistance manner and the
 * direct-pay/card-review keys on the `tuitionAssistance` settings block — so
 * the picker axis (and its persistence) only applies to products that pass
 * this check.
 */
/** Whether the selected benefit set includes Tuition Assistance. */
export function selectionHasTuitionAssistance(productIds: DemoProductId[]): boolean {
  return productIds.includes('tuitionAssistance');
}

/**
 * Whether the selection is "TA-only" in the real-app sense: Tuition Assistance
 * is on and no money/loan benefit rides along (Career Pathways may, matching the
 * canonical `ta-only` scenario). Drives `isTaOnly` on the demo company settings
 * and the Starbucks SCAP program title.
 */
function isTaOnlySelection(features: DemoScenario['features']): boolean {
  return (
    features.tuitionAssistance &&
    !features.studentLoanRepayment &&
    !features.pslf &&
    !features.employerMatch &&
    !features.financialWellness
  );
}

/** Whether every atomic benefit is selected (the old flexible-choice-plan bundle). */
function isAllBenefitsSelected(features: DemoScenario['features']): boolean {
  return DEMO_PRODUCT_IDS.every((key) => features[key]);
}

function getTasScenarioIdForDemoSession(
  productIds: DemoProductId[],
  userScenarioId: DemoUserScenarioId
): DemoTasScenarioId | undefined {
  if (!productIds.includes('tuitionAssistance')) {
    return undefined;
  }

  const userScenario = getDemoUserScenario(userScenarioId);
  const userStateScenario = getDemoScenario(userScenario.scenarioId);
  return (
    userStateScenario.userState.taScenario ?? DEMO_PRODUCTS.tuitionAssistance.defaultTasScenarioId
  );
}

// Map a scenario's feature flags onto the atomic benefit ids (for decoding a
// legacy `demo_scenario` into the product set).
function scenarioFeaturesToProductIds(features: DemoScenario['features']): DemoProductId[] {
  return DEMO_PRODUCT_IDS.filter((key) => features[key]);
}

// Dedupe + canonical order; drops anything that is not a current benefit id.
function normalizeProductIds(productIds: DemoProductId[]): DemoProductId[] {
  return DEMO_PRODUCT_IDS.filter((key) => productIds.includes(key));
}

// Decode one `demo_product` CSV token: a current atomic id (1 benefit) or a
// legacy bundle id (its benefit set). `hasOwn` guards prototype-chain tokens
// (e.g. `__proto__`), matching the existing read-path hardening.
function decodeProductToken(token: string): DemoProductId[] {
  if (hasOwn(DEMO_PRODUCTS, token)) {
    return [token];
  }
  if (hasOwn(LEGACY_PRODUCT_FLAGS, token)) {
    return LEGACY_PRODUCT_FLAGS[token] ?? [];
  }
  return [];
}

export function getDemoProductIdsFromUrl(): DemoProductId[] {
  if (typeof window === 'undefined') {
    return [...DEMO_PRODUCT_IDS];
  }

  const params = new URLSearchParams(window.location.search);
  const raw = params.get('demo_product') ?? localStorage.getItem('reimagined-demo-product');
  if (raw) {
    const ids = normalizeProductIds(raw.split(',').flatMap(decodeProductToken));
    if (ids.length) {
      const csv = ids.join(',');
      if (localStorage.getItem('reimagined-demo-product') !== csv) {
        localStorage.setItem('reimagined-demo-product', csv);
      }
      return ids;
    }
  }

  // Legacy fallback: decode an explicit demo_scenario into the benefit set so the
  // in-page banner scenario axis (and old links) still resolve to products.
  const scenarioId =
    (params.get('demo_scenario') as DemoScenarioId | null) ??
    (localStorage.getItem('reimagined-demo-scenario') as DemoScenarioId | null);
  if (scenarioId && hasOwn(DEMO_SCENARIOS, scenarioId)) {
    const ids = scenarioFeaturesToProductIds(DEMO_SCENARIOS[scenarioId].features);
    if (ids.length) {
      return ids;
    }
  }

  return [...DEMO_PRODUCT_IDS];
}

export function getCurrentDemoProductIds(): DemoProductId[] {
  return getDemoProductIdsFromUrl();
}

export function getDemoRoleIdFromUrl(): DemoSessionRoleId {
  if (typeof window === 'undefined') {
    return 'employee';
  }

  const params = new URLSearchParams(window.location.search);
  const isDemoMode = params.get('demo') === 'true' || hasStickyDemoMode(window.location.pathname);
  const role = params.get('demo_role') as DemoSessionRoleId | null;
  if (isDemoMode && role && hasOwn(DEMO_SESSION_ROLES, role)) {
    localStorage.setItem('reimagined-demo-role', role);
    return role;
  }

  const stored = localStorage.getItem('reimagined-demo-role') as DemoSessionRoleId | null;
  if (isDemoMode && stored && hasOwn(DEMO_SESSION_ROLES, stored)) {
    return stored;
  }

  const currentRelationshipType = localStorage.getItem('role');
  const roleForRelationship = DEMO_SESSION_ROLE_LIST.find(
    (item) => item.relationshipType === currentRelationshipType
  );
  return roleForRelationship?.id ?? 'employee';
}

export function getCurrentDemoRoleId(): DemoSessionRoleId {
  return getDemoRoleIdFromUrl();
}

function getDefaultUserScenarioIdForProducts(productIds: DemoProductId[]): DemoUserScenarioId {
  // The all-benefits set keeps the flexible-choice persona; any narrower set
  // gets the generic power-user (no single product maps to a persona anymore).
  return productIds.length === DEMO_PRODUCT_IDS.length ? 'flex-choice-hsa' : 'power-user';
}

export function getDemoUserScenarioIdFromUrl(): DemoUserScenarioId {
  if (typeof window === 'undefined') {
    return 'power-user';
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('demo_user_scenario') as DemoUserScenarioId | null;
  if (fromUrl && hasOwn(DEMO_USER_SCENARIOS, fromUrl)) {
    localStorage.setItem('reimagined-demo-user-scenario', fromUrl);
    return fromUrl;
  }

  const stored = localStorage.getItem('reimagined-demo-user-scenario') as DemoUserScenarioId | null;
  if (stored && hasOwn(DEMO_USER_SCENARIOS, stored)) {
    return stored;
  }

  return getDefaultUserScenarioIdForProducts(getDemoProductIdsFromUrl());
}

export function getCurrentDemoUserScenarioId(): DemoUserScenarioId {
  return getDemoUserScenarioIdFromUrl();
}

export function getDemoTaPaymentModelIdFromUrl(): DemoTaPaymentModelId {
  if (typeof window === 'undefined') {
    return 'reimbursement';
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('demo_ta_payment_model') as DemoTaPaymentModelId | null;
  if (fromUrl && hasOwn(DEMO_TA_PAYMENT_MODELS, fromUrl)) {
    localStorage.setItem('reimagined-demo-ta-payment-model', fromUrl);
    return fromUrl;
  }

  const stored = localStorage.getItem(
    'reimagined-demo-ta-payment-model'
  ) as DemoTaPaymentModelId | null;
  if (stored && hasOwn(DEMO_TA_PAYMENT_MODELS, stored)) {
    return stored;
  }

  return 'reimbursement';
}

export function getCurrentDemoTaPaymentModelId(): DemoTaPaymentModelId {
  return getDemoTaPaymentModelIdFromUrl();
}

export function getDemoDebtLevel(debtLevelId: DemoDebtLevelId): DemoDebtLevel {
  // Defensive default: pre-debt-axis session stubs / stale storage can hand
  // an absent or unknown id at runtime despite the Required<> type — resolve
  // to the axis's documented default instead of exploding every consumer
  // (a throw here takes down the whole scenario resolve).
  return DEMO_DEBT_LEVELS[debtLevelId] ?? DEMO_DEBT_LEVELS['not-linked'];
}

export function getDemoDebtLevelIdFromUrl(): DemoDebtLevelId {
  if (typeof window === 'undefined') {
    return 'not-linked';
  }

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('demo_debt') as DemoDebtLevelId | null;
  if (fromUrl && hasOwn(DEMO_DEBT_LEVELS, fromUrl)) {
    localStorage.setItem('reimagined-demo-debt-level', fromUrl);
    return fromUrl;
  }

  const stored = localStorage.getItem('reimagined-demo-debt-level') as DemoDebtLevelId | null;
  if (stored && hasOwn(DEMO_DEBT_LEVELS, stored)) {
    return stored;
  }

  return 'not-linked';
}

export function getCurrentDemoDebtLevelId(): DemoDebtLevelId {
  return getDemoDebtLevelIdFromUrl();
}

export function getCurrentDemoDebtLevel(): DemoDebtLevel {
  return getDemoDebtLevel(getCurrentDemoDebtLevelId());
}

function buildDemoCompanySettings(selection: DemoSettingsSelection): DemoCompanySettings {
  const client = getDemoData(selection.clientId).client;
  const scenario = demoScenarioForSelection(selection);
  const allBenefits = isAllBenefitsSelected(scenario.features);
  const paymentModel = selection.taPaymentModelId;
  const subDomain = client.id;
  // Employer-archetype-derived attrs (independent of which products are on):
  // non-profit + government personas are PSLF-eligible / tax-exempt, for-profit
  // are not. This is the real-world employer shape the persona axis carries.
  const employerType = getDemoEmployerType(client.id);
  const pslfEligible = employerType !== 'for-profit';
  const supportDisabled = {
    isEnabled: false,
  };

  return {
    id: `demo-company-setting-${client.id}`,
    subDomain,
    partnerType: pslfEligible ? 'PSLF_ELIGIBLE' : 'PRIVATE',
    allowFamilyPlan: allBenefits,
    newDesign: true,
    returnToPartnerUrl: {
      base: 'example.com',
      subdomain: subDomain,
      path: 'benefits',
    },
    register: {
      'is-email-register-allowed': true,
      'show-countdown': false,
      'is-payroll-id-and-last-name-allowed': false,
      'require-custom-terms': false,
      'custom-terms-text': '',
      'is-self-register-allowed': true,
      'self-register-domain': '',
    },
    security: {
      passwordMinLength: 8,
    },
    modules: {
      pslf: {
        groups: [],
        enabled: scenario.features.pslf,
        employer: {
          services: [],
          isPolitical: false,
          isTaxExempt: pslfEligible,
          isGovernment: employerType === 'government',
          isNotForProfit: employerType === 'non-profit',
          legalName: client.name,
          fein: '00-0000000',
          website: `https://${subDomain}.example.com`,
          address: {
            addressLine1: '123 Demo Way',
            addressLine2: '',
            city: 'Demo City',
            state: 'CA',
            postalCode: '90000',
          },
        },
        enabledOn: '2025-01-01',
        isNonPartnerEmployer: false,
        isEnabledPriorEmployer: pslfEligible,
        isNonPartnerApprover: false,
      },
      syf: {
        isEnabled: scenario.features.employerMatch,
      },
      support: {
        faqs: supportDisabled,
        zendesk: supportDisabled,
        tioEmail: supportDisabled,
        tioPhone: supportDisabled,
      },
    },
    tuitionAssistance: {
      taOnlyPlanId: `demo-ta-${client.id}`,
      // Mentoring is part of the TA story (sales feedback): the TA Mentoring
      // dashboard card must show for a plain Tuition Assistance selection, not
      // only when the separate Career Pathways product is also ticked.
      isTaMentoringEnabled: scenario.features.tuitionAssistance || scenario.features.careerPathways,
      isTaOnly: isTaOnlySelection(scenario.features),
      isTaEnabled: scenario.features.tuitionAssistance,
      eligibilityWaitingPeriodDays: 0,
      isDependentProgramsEnabled: allBenefits,
      isDirectPayEnabled: paymentModel === 'prepayment',
      isPartnerMessagingEnabled: false,
      isPartnerMessagingEmailEnabled: false,
      enableAgentRecommendations: scenario.features.careerPathways,
      hideAnnualBenefitsCard: false,
      isCardUsageReviewEnabled: paymentModel === 'prepayment',
    },
    lpn: {
      isEnabled: scenario.features.tuitionAssistance,
    },
    quickSight: {
      dashboards: [],
    },
  };
}

function buildDemoPlans(): DemoPlanSummary[] {
  const scenario = getCurrentDemoScenario();
  const client = getCurrentDemoClientData().client;
  const plans: DemoPlanSummary[] = [];

  if (scenario.features.financialWellness) {
    plans.push({
      id: `demo-wellness-plan-${client.id}`,
      name: `${client.name} Financial Wellness`,
      cadence: 'NEVER',
      isWellness: true,
      annualLimit: 0,
      lifetimeLimit: 0,
      tiers: [],
    });
  }

  if (scenario.features.studentLoanRepayment || scenario.features.employerMatch) {
    plans.push({
      id: `demo-contribution-plan-${client.id}`,
      name: `${client.name} Student Loan Benefit`,
      cadence: 'MONTHLY',
      isWellness: false,
      annualLimit: 5250,
      lifetimeLimit: 20000,
      tiers: [{ amount: scenario.features.employerMatch ? 100 : 125 }],
    });
  }

  return plans;
}

/** Every module flag gated by the selection's products. */
export function demoCompanySettingsForSelection(
  selection: DemoSettingsSelection
): DemoCompanySettings {
  return buildDemoCompanySettings(selection);
}

/** {@link demoCompanySettingsForSelection} for whatever the browser has stored. */
export function getCurrentDemoCompanySettings(): DemoCompanySettings {
  return buildDemoCompanySettings({
    clientId: getCurrentDemoClientId(),
    productIds: getCurrentDemoProductIds(),
    taPaymentModelId: getCurrentDemoTaPaymentModelId(),
  });
}

export function getCurrentDemoPlans(): DemoPlanSummary[] {
  return buildDemoPlans();
}

export function getCurrentDemoPreRegisterCompany(): DemoPreRegisterCompany {
  const client = getCurrentDemoClientData().client;
  const settings = getCurrentDemoCompanySettings();
  const scenario = getCurrentDemoScenario();

  return {
    id: `demo-company-${client.id}`,
    name: client.name,
    launchDate: '2020-01-01',
    programTitle:
      isTaOnlySelection(scenario.features) && client.id === 'starbucks'
        ? 'Starbucks College Achievement Plan'
        : 'Education Assistance Program',
    taEnabled: scenario.features.tuitionAssistance,
    settings,
  };
}

export function getCurrentDemoCompany(): DemoPartnerCompany {
  const preRegisterCompany = getCurrentDemoPreRegisterCompany();
  const settings = getCurrentDemoCompanySettings();

  return {
    ...preRegisterCompany,
    settings,
    companySetting: settings,
    plans: getCurrentDemoPlans(),
  };
}

/**
 * Resolve the EFFECTIVE session axes for a selection — the single place the
 * apply path decides what persists, shared by {@link buildDemoSessionUrl} (URL
 * params) and {@link applyDemoSession} (localStorage) so the two stores can
 * never disagree:
 *
 * - User scenario: the personas are PARTICIPANT-only employee journeys. The
 *   picker hides the section for supervisor/admin, but a previously chosen or
 *   stored persona would still ride along and leak into the fabricated `/me`
 *   identity name — so non-participant sessions persist NO persona, and the TA
 *   sub-scenario derives from the product alone (no persona override).
 * - TA payment model: only meaningful when the product's scenario enables
 *   tuition assistance (it feeds the direct-pay/card-review keys on the
 *   `tuitionAssistance` settings block); for non-TA products a stale or
 *   hand-edited non-default model is coerced back to the default.
 */
function resolveDemoSessionAxes(selection: DemoSessionSelection): {
  productIds: DemoProductId[];
  role: DemoSessionRole;
  userScenarioId?: DemoUserScenarioId;
  taPaymentModelId: DemoTaPaymentModelId;
  tasScenarioId?: DemoTasScenarioId;
  pslfScenarioId?: DemoPslfScenarioId;
  debtLevelId: DemoDebtLevelId;
} {
  // Normalize + enforce the non-empty invariant (an empty/invalid set would
  // synthesize an all-false scenario, hiding every surface).
  const requested = normalizeProductIds(selection.productIds);
  const productIds = requested.length ? requested : [...DEMO_PRODUCT_IDS];
  const role = getDemoSessionRole(selection.roleId);
  const userScenarioId =
    role.relationshipType === 'PARTICIPANT'
      ? (selection.userScenarioId ?? getDefaultUserScenarioIdForProducts(productIds))
      : undefined;
  const taPaymentModelId = selectionHasTuitionAssistance(productIds)
    ? (selection.taPaymentModelId ?? 'reimbursement')
    : 'reimbursement';
  // The picker's explicit TA-scenario pick beats derivation — participant sessions with TA only, mirroring the userScenarioId role gate above.
  const tasScenarioId = selectionHasTuitionAssistance(productIds)
    ? userScenarioId
      ? (selection.tasScenarioId ?? getTasScenarioIdForDemoSession(productIds, userScenarioId))
      : DEMO_PRODUCTS.tuitionAssistance.defaultTasScenarioId
    : undefined;
  // PSLF only when selected AND the persona's employer is PSLF-eligible
  // (for-profit personas can't qualify) — keeps the persisted demo_pslf_scenario
  // coherent with personaMaskedFeatures masking pslf out of the composite.
  const pslfScenarioId =
    productIds.includes('pslf') && isPslfEligibleClient(selection.clientId)
      ? (selection.pslfScenarioId ?? DEMO_PRODUCTS.pslf.defaultPslfScenarioId)
      : undefined;
  // Debt is an orthogonal, always-present axis (no product/persona guard);
  // 'not-linked' is the default "no linked loans" state.
  const debtLevelId = selection.debtLevelId ?? 'not-linked';

  return {
    productIds,
    role,
    userScenarioId,
    taPaymentModelId,
    tasScenarioId,
    pslfScenarioId,
    debtLevelId,
  };
}

export function buildDemoSessionUrl(currentHref: string, selection: DemoSessionSelection): string {
  const {
    productIds,
    userScenarioId,
    taPaymentModelId,
    tasScenarioId,
    pslfScenarioId,
    debtLevelId,
  } = resolveDemoSessionAxes(selection);
  const url = new URL(currentHref);

  url.searchParams.set('demo', 'true');
  url.searchParams.set('demo_client', selection.clientId);
  url.searchParams.set('demo_product', productIds.join(','));
  url.searchParams.set('demo_role', selection.roleId);
  if (userScenarioId) {
    url.searchParams.set('demo_user_scenario', userScenarioId);
  } else {
    url.searchParams.delete('demo_user_scenario');
  }
  url.searchParams.set('demo_ta_payment_model', taPaymentModelId);
  url.searchParams.set('demo_debt', debtLevelId);
  // Base scenario for the in-page banner + mega-menu nav (which key on a single
  // scenario id); the composite features come from demo_product, not this.
  url.searchParams.set('demo_scenario', resolveBaseScenarioId(productIds));

  if (tasScenarioId) {
    url.searchParams.set('demo_ta_scenario', tasScenarioId);
  } else {
    url.searchParams.delete('demo_ta_scenario');
  }

  if (pslfScenarioId) {
    url.searchParams.set('demo_pslf_scenario', pslfScenarioId);
  } else {
    url.searchParams.delete('demo_pslf_scenario');
  }

  return url.toString();
}

export function applyDemoSession(selection: DemoSessionSelection): void {
  if (typeof window === 'undefined') {
    return;
  }

  const {
    productIds,
    role,
    userScenarioId,
    taPaymentModelId,
    tasScenarioId,
    pslfScenarioId,
    debtLevelId,
  } = resolveDemoSessionAxes(selection);

  localStorage.setItem('reimagined-demo-mode', 'true');
  localStorage.setItem('reimagined-demo-client', selection.clientId);
  localStorage.setItem('reimagined-demo-product', productIds.join(','));
  localStorage.setItem('reimagined-demo-role', selection.roleId);
  if (userScenarioId) {
    localStorage.setItem('reimagined-demo-user-scenario', userScenarioId);
  } else {
    // Clear any stale persona so the sticky read path cannot resurrect it for
    // a non-participant session.
    localStorage.removeItem('reimagined-demo-user-scenario');
  }
  localStorage.setItem('reimagined-demo-ta-payment-model', taPaymentModelId);
  localStorage.setItem('reimagined-demo-debt-level', debtLevelId);
  localStorage.setItem('reimagined-demo-scenario', resolveBaseScenarioId(productIds));
  // Overload the shared `role` key with the demo relationship type, and mark that
  // we did so — clearDemoSession() restores (clears) `role` only when this marker
  // is present, so a real user's role selection is never wiped by the in-page
  // toggle or a pure `?demo=true` view (which never overload `role`).
  localStorage.setItem('role', role.relationshipType);
  localStorage.setItem('reimagined-demo-role-overloaded', 'true');

  if (tasScenarioId) {
    localStorage.setItem('reimagined-demo-ta-scenario', tasScenarioId);
  } else {
    localStorage.removeItem('reimagined-demo-ta-scenario');
  }

  if (pslfScenarioId) {
    localStorage.setItem('reimagined-demo-pslf-scenario', pslfScenarioId);
  } else {
    localStorage.removeItem('reimagined-demo-pslf-scenario');
  }

  window.location.assign(
    buildDemoSessionUrl(resolveDemoEntryHref(window.location.href, selection.roleId), selection)
  );
}

/**
 * Where an applied demo session lands. Applying from a demoable route keeps
 * the rep on the page they were looking at; applying from anywhere else must
 * NAVIGATE INTO the demo — reloading in place used to arm the sticky flag on
 * a non-demoable route, booting straight into the PAUSED
 * "showing real data" state, which read as a broken demo. Participants land
 * on the shipped dashboard, other roles on the admin experience — mirroring
 * the app's own role redirect (routes/authenticated.ts).
 */
export function resolveDemoEntryHref(currentHref: string, roleId: DemoSessionRoleId): string {
  const url = new URL(currentHref);
  // Keep the current page only when it suits the picked role (mirrors landingRoute).
  if (roleId === 'employee') {
    const segments = url.pathname.split('/').filter(Boolean);
    // The /reimagined/admin/* subtree is admin-only despite living in the sandbox.
    const isReimaginedAdmin = segments[0] === 'reimagined' && segments[1] === 'admin';
    return isShippedParticipantRoutePath(url.pathname) ||
      (isReimaginedRoutePath(url.pathname) && !isReimaginedAdmin)
      ? currentHref
      : new URL('/dashboard', url.origin).toString();
  }
  return isAdminRoutePath(url.pathname) ? currentHref : new URL('/admin', url.origin).toString();
}
